<template>
  <div class="d-flex flex-nowrap" style="height: 100vh; overflow: hidden;">
    <div class="d-flex flex-column flex-shrink-0 bg-light" style="width: 6rem;">
        <a href="/" class="d-block p-3 link-dark text-decoration-none border-bottom" title="Icon-only" data-bs-toggle="tooltip" data-bs-placement="right">
        <img src="../assets/logo.png" class="img-fluid">
        <span class="visually-hidden">LIFeLiDAR SAS</span>
      </a>
      <ul class="nav nav-pills nav-flush flex-column mb-auto text-center">
        <li v-if="isLoggedIn">
          <router-link to="/map" class="nav-link py-3 border-bottom rounded-0" :class="currentRouteName=='map' ? 'active' : ''" title="Map" data-bs-toggle="tooltip" data-bs-placement="right">
            <div class="row">
              <svg class="bi pe-none" width="24" height="24" role="img" aria-label="Charts"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#globe-europe-africa"/></svg>
            </div>
            <div class="row">
              <small>Map</small>
            </div>
          </router-link>
        </li>
        <li v-if="isLoggedIn">
          <router-link to="/charts" class="nav-link py-3 border-bottom rounded-0" :class="currentRouteName=='charts' ? 'active' : ''" title="Charts" data-bs-toggle="tooltip" data-bs-placement="right">
            <div class="row">
              <svg class="bi pe-none" width="24" height="24" role="img" aria-label="Charts"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#graph-up"/></svg>
            </div>
            <div class="row">
              <small>Charts</small>
            </div>
          </router-link>
        </li>
        <li v-if="isLoggedIn">
          <router-link to="/fluo" class="nav-link py-3 border-bottom rounded-0" :class="currentRouteName=='fluo' ? 'active' : ''" title="Fluo Spectrum" data-bs-toggle="tooltip" data-bs-placement="right">
            <div class="row">
              <svg class="bi pe-none" width="24" height="24" role="img" aria-label="Fluo Spectrum"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#bar-chart-line"/></svg>
            </div>
            <div class="row">
              <small>Fluo Spectrum</small>
            </div>
          </router-link>
        </li>
        <li v-if="isLoggedIn">
          <a href="/" class="nav-link py-3 border-bottom rounded-0" :class="currentRouteName=='/' ? 'active' : ''" title="Colour Spectrum" data-bs-toggle="tooltip" data-bs-placement="right">
            <div class="row">
              <svg class="bi pe-none" width="24" height="24" role="img" aria-label="Colour Spectrum"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#palette-fill"/></svg>
            </div>
            <div class="row">
              <small>Colour Spectrum</small>
            </div>
          </a>
        </li>
        <li v-if="isLoggedIn">
          <router-link to="/profiling" class="nav-link py-3 border-bottom rounded-0" :class="currentRouteName=='profiling' ? 'active' : ''" title="Profiling" data-bs-toggle="tooltip" data-bs-placement="right">
            <div class="row">
              <svg class="bi pe-none" width="24" height="24" role="img" aria-label="Profiling"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#bar-chart-steps"/></svg>
            </div>
            <div class="row">
              <small>Profiling</small>
            </div>
          </router-link>
        </li>
        <li v-if="isLoggedIn">
          <a href="#" @click.prevent="openPrintReport" class="nav-link py-3 border-bottom rounded-0" title="Print Report" role="button" data-bs-toggle="tooltip" data-bs-placement="right">
            <div class="row">
              <svg class="bi pe-none" width="24" height="24" role="img" aria-label="Print Report"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#camera"/></svg>
            </div>
            <div class="row">
              <small>Print Report</small>
            </div>
          </a>
        </li>
        <li v-if="isLoggedIn">
          <a href="#" @click.prevent="toggleOptionPanel" class="nav-link py-3 border-bottom rounded-0" :class="{ active: isOffcanvas }" title="Parameters" role="button">
            <div class="row">
              <svg class="bi pe-none" width="24" height="24" role="img" aria-label="Home"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#three-dots"/></svg>
            </div>
            <div class="row">
              <small>Option</small>
            </div>
          </a>
          <CalibrationFormModal ref="calibrationFormModal" @save="saveCalibration" />
          <CalibrationTableModal 
            ref="calibrationTableModal" 
            :calibrations="calibrations" 
            @edit="editCalibration" 
            @delete="deleteCalibration" 
          />
          <EditParameterNamesModal
            ref="editNamesModal"
            :titles="paramTitles"
            @save="updateTitles"
          />
          <PrintReportModal
            ref="printReportModal"
            :site-name="selected_area_name"
          />
          <AddWaterMaskModal
            ref="addWaterMaskModal"
            @request-polygon="handleRequestPolygon"
            @saved="handleWaterMaskSaved"
            @deleted="handleWaterMaskDeleted"
            @preview="handlePreviewArea"
          />
        </li>

      </ul>
      <!-- The auth dropdown (Profile/Sign in/out) is hidden in local mode -->
      <div v-if="!isLocalMode" class="dropdown border-top">
        <a href="#" class="d-flex align-items-center justify-content-center p-3 link-dark text-decoration-none dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
          <svg class="bi pe-none" width="24" height="24" role="img" aria-label="User"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#person-circle"/></svg>
        </a>
        <ul class="dropdown-menu text-small shadow">
          <!-- Lidar selector: only when the account can see more than one lidar -->
          <template v-if="isLoggedIn && hasLidarChoice">
            <li><h6 class="dropdown-header">Lidar</h6></li>
            <li v-for="l in lidars" :key="l.id">
              <a
                class="dropdown-item"
                :class="{ active: l.id === selectedLidarId }"
                href="#"
                @click.prevent="selectLidar(l.id)"
              >#{{ l.id }} {{ l.name }}</a>
            </li>
            <li><hr class="dropdown-divider"></li>
          </template>
          <li v-if="isLoggedIn" ><a class="dropdown-item" href="/profile">Profile</a></li>
          <li v-if="isLoggedIn && isAdmin"><a class="dropdown-item" href="/admin">Administration</a></li>
          <li><hr class="dropdown-divider"></li>
          <li v-if="isLoggedIn"><a class="dropdown-item" href="#" @click="logout()">Sign out</a></li>
          <li v-else><a class="dropdown-item" href="/login">Sign in</a></li>
        </ul>
      </div>
    </div>
    <div class="b-example-divider b-example-vr"></div>
    <div
      id="params-panel"
      class="flex-shrink-0 bg-light border-end"
      :class="isOffcanvas && isLoggedIn ? 'overflow-auto' : 'overflow-hidden'"
      :style="{ width: (isOffcanvas && isLoggedIn) ? offcanvasWidth + 'px' : '0px', transition: 'width 0.2s ease' }"
    >
            <div v-show="currentRouteName=='map'" class="offcanvas-body">
              <div class="accordion" id="accordionPanelsStayOpenExample-map">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne-map"
                     aria-expanded="true" aria-controls="panelsStayOpen-collapseOne-map">
                      Data
                    </button>
                  </h2>
                  <div id="panelsStayOpen-collapseOne-map" class="accordion-collapse collapse show">
                    <div class="accordion-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row mb-1">
                            <VueDatePicker id="manual-map" :key="datePickerKey" :model-value="date" enable-seconds range :timezone="{ convertModel: true, timezone: 'UTC' }" @update:model-value="setDate" style="--dp-font-size: 0.85rem" :disabled="isLoadingData || isDrawing"/>
                            <!-- <VueDatePicker id="manual-map" :model-value="date" enable-seconds :format="format" range :timezone="{ convertModel: true, timezone: 'UTC' }" @update:model-value="setDate" e/> -->
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col">
                              <div class="btn-group" role="group" aria-label="Basic radio toggle button group" >
                                <input type="radio" class="btn-check" name="btnradio-map" id="btnradio1-map" autocomplete="off" @click="set_custom()" :checked="is_custom_data" :disabled="isLoadingData  || isDrawing">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio1-map">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#activity"/>
                                  </svg>
                                  Custom
                                </label>
                                <input type="radio" class="btn-check" name="btnradio-map" id="btnradio2-map" autocomplete="off" @click="set_complete()" :checked="!is_custom_data" :disabled="isLoadingData  || isDrawing">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio2-map">
                                  <span v-if="isLoadingData && isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                  <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#database"/>
                                  </svg>
                                  Complete
                                </label>
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchIsLive-map" v-model="is_live" :disabled="!is_ssc">
                                <label class="form-check-label" for="flexSwitchIsLive-map">live</label>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col-sm-4">
                              <label for="colFormSiteLaser-map" class="col-form-label-sm">
                                Site
                              </label>
                            </div>
                            <div class="col-sm-8">
                              <select class="form-select form-select-sm" aria-label="Default select example" id="colFormSiteLaser-map" placeholder="Choose site..." v-model="selected_area_name" @change="handleAreaChange">
                                <option selected>Choose site...</option>
                                <option v-for="area in areas" :value="area.name" :key="area">
                                  {{ area.name }}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col-sm-4">
                              <label for="colFormLabelLaser-map" class="col-form-label-sm">
                                Laser, nm
                              </label>
                            </div>
                            <div class="col-sm-8">
                              <select class="form-select form-select-sm" id="colFormLabelLaser-map" v-model="laser">
                                <option v-for="laser in lasers" :value="laser" :key="laser">
                                  {{ laser }}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div v-if="is_live" class="row row-cols-auto mb-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#clock-history"/>
                                </svg>
                                Interval, s
                              </span>
                              <input type="number" class="form-control" min="0" v-model="intervalTime" :disabled="isDrawing">
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button @click="get_params()" class="btn btn-outline-primary" :disabled="isLoadingData || isDrawing">
                                <span v-if="isLoadingData && !isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#search"/>
                                </svg>
                                <!-- <span role="status">Loading...</span> -->
                                Search
                              </button>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                     data-bs-target="#panelsStayOpen-collapseTwo-map" aria-expanded="false" aria-controls="panelsStayOpen-collapseTwo-map">
                      Calibrations
                    </button>
                  </h2>
                  <div id="panelsStayOpen-collapseTwo-map" class="accordion-collapse collapse" :class="(mins1.length && mins2.length) ? 'show' : ''">
                    <div class="accordion-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="openDescriptionModal">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#table"/></svg>
                                Calibration Curve
                              </button>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="openAddCalibrationModal">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/></svg>
                                Add new
                              </button>
                            </div>
                          </div>
                          <div class="row row-cols-auto mt-1">
                            <div class="col">
                              <div class="form-check form-switch switch-red mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault2-map" v-model="r_vm" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchCheckDefault2-map">{{ paramTitles[0] }}</label>
                              </div>
                            </div>
                          </div>
                          <!-- <div v-if="r_vm" class="row g-0 align-items-center mb-2">
                              <div class="col">
                                <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_cal_r" :disabled="!cals.length || isDrawing" @change="min_changed('', '')">
                                  <option v-for="cal in cals" :value="cal" :key="cal">
                                    {{ cal }}
                                  </option>
                                </select>
                              </div>
                              <div class="col-auto divider">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                                </svg>
                              </div>
                              <div class="col">
                                  <div class="row g-0 mb-1">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_min1_r" :disabled="!mins1_r.length || isDrawing" @change="min_changed(selected_min1_r, 'r1')">
                                          <option v-for="min in mins1_r" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                                  <div class="row g-0">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_min2_r" :disabled="!mins2_r.length || isDrawing" @change="min_changed(selected_min2_r, 'r2')">
                                          <option v-for="min in mins2_r" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                              </div>
                          </div> -->
                          <div v-if="r_vm">
                            <div class="row row-cols-auto mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm1-map" class="col-form-label-sm">
                                  Signal
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm1-map" v-model="selectedSigR" @change="onSignalChanged('r')">
                                  <option v-for="sig in signals" :value="sig" :key="sig">
                                    {{ sig }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm2-map" class="col-form-label-sm">
                                  Normalization
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm2-map" v-model="selectedNormR" :disabled="!selectedSigR || loadingNormsR">
                                  <option value="No">No</option>
                                  <option v-for="norm in compatibleNormsR" :value="norm" :key="norm">
                                    {{ norm }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm3-map" class="col-form-label-sm">
                                  Calibration Curve
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" v-model="selectedCalRId" id="colFormLabelSm3-map" :key="calibrations.length">
                                  <option value="">No</option>
                                  <option v-for="cal in calibrations" :key="cal.id" :value="cal.id">
                                    {{ cal.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1">
                            <div class="col">
                              <div class="form-check form-switch switch-green mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault3-map" v-model="g_vm" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchCheckDefault3-map">{{ paramTitles[1] }}</label>
                              </div>
                            </div>
                          </div>
                          <!-- <div v-if="g_vm" class="row g-0 align-items-center mb-2">
                              <div class="col">
                                <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_cal_g" :disabled="!cals.length || isDrawing"  @change="min_changed('', '')">
                                  <option v-for="cal in cals" :value="cal" :key="cal">
                                    {{ cal }}
                                  </option>
                                </select>
                              </div>
                              <div class="col-auto divider">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                                </svg>
                              </div>
                              <div class="col">
                                  <div class="row g-0 mb-1">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_min1_g" :disabled="!mins1_g.length || isDrawing" @change="min_changed(selected_min1_g, 'g1')">
                                          <option v-for="min in mins1_g" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                                  <div class="row g-0">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_min2_g" :disabled="!mins2_g.length || isDrawing" @change="min_changed(selected_min2_g, 'g2')">
                                          <option v-for="min in mins2_g" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                              </div>
                          </div> -->
                          <div v-if="g_vm">
                            <div class="row row-cols-auto mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm10-map" class="col-form-label-sm">
                                  Signal
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm10-map" v-model="selectedSigG" @change="onSignalChanged('g')">
                                  <option v-for="sig in signals" :value="sig" :key="sig">
                                    {{ sig }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm11-map" class="col-form-label-sm">
                                  Normalization
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm11-map" v-model="selectedNormG" :disabled="!selectedSigG || loadingNormsG">
                                  <option value="No">No</option>
                                  <option v-for="norm in compatibleNormsG" :value="norm" :key="norm">
                                    {{ norm }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm12-map" class="col-form-label-sm">
                                  Calibration Curve
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" v-model="selectedCalGId" id="colFormLabelSm12-map" :key="calibrations.length">
                                  <option value="">No</option>
                                  <option v-for="cal in calibrations" :key="cal.id" :value="cal.id">
                                    {{ cal.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1">
                            <div class="col">
                              <div class="form-check form-switch switch-blue mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault4-map" v-model="b_vm" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchCheckDefault4-map">{{ paramTitles[2] }}</label>
                              </div>
                            </div>
                          </div>
                          <!-- <div v-if="b_vm" class="row g-0 align-items-center mb-2">
                              <div class="col">
                                <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_cal_b" :disabled="!cals.length || isDrawing"  @change="min_changed('', '')">
                                  <option v-for="cal in cals" :value="cal" :key="cal">
                                    {{ cal }}
                                  </option>
                                </select>
                              </div>
                              <div class="col-auto divider">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                                </svg>
                              </div>
                              <div class="col">
                                  <div class="row g-0 mb-1">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_min1_b" :disabled="!mins1_b.length || isDrawing" @change="min_changed(selected_min1_b, 'b1')">
                                          <option v-for="min in mins1_b" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                                  <div class="row g-0">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_min2_b" :disabled="!mins2_b.length || isDrawing" @change="min_changed(selected_min2_b, 'b2')">
                                          <option v-for="min in mins2_b" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                              </div>
                          </div> -->
                          <div v-if="b_vm">
                            <div class="row row-cols-auto mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm14-map" class="col-form-label-sm">
                                  Signal
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm14-map" v-model="selectedSigB" @change="onSignalChanged('b')">
                                  <option v-for="sig in signals" :value="sig" :key="sig">
                                    {{ sig }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm15-map" class="col-form-label-sm">
                                  Normalization
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm15-map" v-model="selectedNormB" :disabled="!selectedSigB || loadingNormsB">
                                  <option value="No">No</option>
                                  <option v-for="norm in compatibleNormsB" :value="norm" :key="norm">
                                    {{ norm }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm16-map" class="col-form-label-sm">
                                  Calibration Curve
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" v-model="selectedCalBId" id="colFormLabelSm16-map" :key="calibrations.length">
                                  <option value="">No</option>
                                  <option v-for="cal in calibrations" :key="cal.id" :value="cal.id">
                                    {{ cal.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button class="btn btn-outline-primary">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/></svg>
                                Add Measuring Parameter
                              </button>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="openEditModal">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#gear"/></svg>
                                Edit
                              </button>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                   <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                      data-bs-target="#panelsStayOpen-collapseThree1-map" aria-expanded="false" aria-controls="panelsStayOpen-collapseThree1-map">
                       Visualisation
                    </button>
                   </h2>
                   <div id="panelsStayOpen-collapseThree1-map" class="accordion-collapse collapse">
                     <div class="accordion-body">
                       <ul class="list-group list-group-flush">
                        <li v-if="(r_vm && !(g_vm || b_vm))" class="list-group-item">
                          <div class="row row-cols-auto">
                            <label for="colFormLabelSm-map" class="col mt-1 pl-1 col-form-label col-form-label-sm">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#brightness-alt-high"/>
                                </svg>
                                {{ paramTitles[0] }}
                            </label>
                          </div>
                          <div class="row row-cols-auto">
                            <div class="col mt-1">
                              <div class="input-group input-group-sm">
                                <span class="input-group-text">
                                  <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#palette"/>
                                  </svg>
                                  Color step
                                </span>
                                <input type="number" v-model="redPaletteConfig.numColors" min="2" max="50" class="form-control">
                              </div>
                            </div>
                            <div class="col">
                              <input type="color" v-model="redPaletteConfig.baseColor" class="form-control form-control-color">
                            </div>
                          </div>
                        </li>
                        <li v-if="(g_vm && !(r_vm || b_vm))" class="list-group-item">
                          <div class="row row-cols-auto">
                            <label for="colFormLabelSm-map" class="col mt-1 pl-1 col-form-label col-form-label-sm">
                              <svg class="bi" width="16" height="16" fill="currentColor">
                                <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#brightness-alt-high"/>
                              </svg>
                              {{ paramTitles[1] }}
                            </label>
                          </div>
                          <div class="row row-cols-auto">
                            <div class="col mt-1">
                              <div class="input-group input-group-sm">
                                <span class="input-group-text">
                                  <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#palette"/>
                                  </svg>
                                  Color step
                                </span>
                                <input type="number" v-model="greenPaletteConfig.numColors" min="2" max="50" class="form-control">
                              </div>
                            </div>
                            <div class="col">
                              <input type="color" v-model="greenPaletteConfig.baseColor" class="form-control form-control-color">
                            </div>
                          </div>
                        </li>
                        <li v-if="(b_vm && !(r_vm || g_vm))" class="list-group-item">
                          <div class="row row-cols-auto">
                            <label for="colFormLabelSm-map" class="col mt-1 pl-1 col-form-label col-form-label-sm">
                              <svg class="bi" width="16" height="16" fill="currentColor">
                                <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#brightness-alt-high"/>
                              </svg>
                              {{ paramTitles[2] }}
                            </label>
                          </div>
                          <div class="row row-cols-auto">
                            <div class="col mt-1">
                              <div class="input-group input-group-sm">
                                <span class="input-group-text">
                                  <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#palette"/>
                                  </svg>
                                  Color step
                                </span>
                                <input type="number" v-model="bluePaletteConfig.numColors" min="2" max="50" class="form-control">
                              </div>
                            </div>
                            <div class="col">
                              <input type="color" v-model="bluePaletteConfig.baseColor" class="form-control form-control-color">
                            </div>
                          </div>
                        </li>
                        <!-- <li class="list-group-item">
                          <div class="row row-cols-auto mb-2 mt-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#arrows-angle-expand"/>
                                </svg>
                                Point size
                              </span>
                              <input type="number" class="form-control" v-model="hmPointSize" :disabled="isDrawing">
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col">
                              <button class="btn btn-outline-primary" :class=" heatmap_vm ? 'active' : ''"  @click="heatmap_vm = !heatmap_vm" data-bs-toggle="button" autocomplete="off">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#pin-map"/>
                                </svg>
                                Points
                              </button>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary"  @click="is_fit_vm = !is_fit_vm" data-bs-toggle="button" autocomplete="off">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#crosshair"/>
                                </svg>
                                Fit
                              </button>
                            </div>
                          </div>
                         </li> -->
                         <li class="list-group-item">
                          <div class="row row-cols-auto">
                            <div class="col">
                              <div class="input-group input-group-sm mt-1 mb-2">
                                <span class="input-group-text">
                                  <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#aspect-ratio"/>
                                  </svg>
                                  Max distance, meters
                                </span>
                                <input type="number" class="form-control" v-model="maxd" :disabled="isDrawing">
                              </div>
                            </div>
                            <div class="col">
                              <div class="input-group input-group-sm mb-2">
                                <span class="input-group-text">
                                  <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#arrows-angle-expand"/>
                                  </svg>
                                  Scale
                                </span>
                                <input type="number" class="form-control" v-model="scale" :disabled="isDrawing">
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault5-map" v-model="log_vm" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchCheckDefault5-map">log_10 scale</label>
                              </div>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1 mb-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#calculator"/>
                                </svg>
                                Average
                              </span>
                              <input type="number" class="form-control" min="0" v-model="avgTime" :disabled="isDrawing">
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col">
                              <div class="btn-group" role="group" aria-label="Basic radio toggle button group" >
                                <input type="radio" class="btn-check" name="btnradio1-map" id="btnradio3-map" autocomplete="off" checked>
                                <label class="btn btn-outline-primary btn-sm" for="btnradio3-map">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#clock"/>
                                  </svg>
                                  Time
                                </label>
                                <input type="radio" class="btn-check" name="btnradio1-map" id="btnradio4-map" autocomplete="off">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio4-map">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#three-dots"/>
                                  </svg>
                                  Points
                                </label>
                              </div>
                            </div>
                          </div>
                          <div v-if="r_vm" class="row row-cols-auto mt-1 mb-1">
                            <div class="col">
                              <div class="input-group input-group-sm">
                                <div class="input-group-text">
                                  <label v-if="g_vm || b_vm" class="form-check-label" style="margin-right : 1rem">CH1 data limits</label>
                                  <label v-else class="form-check-label" style="margin-right : 1rem">Data limits</label>
                                  <input class="form-check-input mt-0" type="checkbox" v-model="is_r_filter" :disabled="!r_vm">
                                </div>
                                  <input type="number" class="form-control" placeholder="min" min="0" step="0.01" :disabled="!is_r_filter || !r_vm || isDrawing" v-model="r_filter_min">
                                  <input type="number" class="form-control" placeholder="max" step="0.01" :disabled="!is_r_filter || !r_vm || isDrawing" v-model="r_filter_max">
                              </div>
                            </div>
                          </div>
                          <div v-if="g_vm" class="row row-cols-auto mt-1 mb-1">
                            <div class="col">
                              <div class="input-group input-group-sm">
                                <div class="input-group-text">
                                  <label v-if="r_vm || b_vm" class="form-check-label" style="margin-right : 1rem">CH2 data limits</label>
                                  <label v-else class="form-check-label" style="margin-right : 1rem">Data limits</label>
                                  <input class="form-check-input mt-0" type="checkbox" v-model="is_g_filter" :disabled="!g_vm">
                                </div>
                                  <input type="number" class="form-control" placeholder="min" min="0" step="0.01" :disabled="!is_g_filter || !g_vm || isDrawing" v-model="g_filter_min">
                                  <input type="number" class="form-control" placeholder="max" step="0.01" :disabled="!is_g_filter || !g_vm || isDrawing" v-model="g_filter_max">
                              </div>
                            </div>
                          </div>
                          <div v-if="b_vm" class="row row-cols-auto mt-1 mb-1">
                            <div class="col">
                              <div class="input-group input-group-sm">
                                <div class="input-group-text">
                                  <label v-if="g_vm || r_vm" class="form-check-label" style="margin-right : 1rem">CH3 data limits</label>
                                  <label v-else class="form-check-label" style="margin-right : 1rem">Data limits</label>
                                  <input class="form-check-input mt-0" type="checkbox" v-model="is_b_filter" :disabled="!b_vm">
                                </div>
                                  <input type="number" class="form-control" placeholder="min" min="0" step="0.01" :disabled="!is_b_filter || !b_vm || isDrawing" v-model="b_filter_min">
                                  <input type="number" class="form-control" placeholder="max" step="0.01" :disabled="!is_b_filter || !b_vm || isDrawing" v-model="b_filter_max">
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto">
                            <div class="col">
                              <button 
                                @click="draw()" 
                                class="btn" 
                                :class="isStarted ? 'btn-outline-danger' : 'btn-outline-primary'" 
                                :disabled="(isDrawing && !isStarted) || (is_ssc ? !idw_vm : (isLoadingData || !result)) || (!r_vm && !g_vm && !b_vm) || isCancelling || !mins1.length 
                                || !mins2.length || !cals.length"
                                >
                                <span v-if="isCancelling" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <span v-else-if="isDrawing && !isStarted" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <svg v-else-if="isDrawing && isStarted" class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-circle"/>
                                </svg>
                                <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#check2-square"/>
                                </svg>
                                {{ isCancelling ? 'Cancelling...' : idwText }}
                              </button>
                            </div>
                          </div>
                          <div v-if="is_live && isDrawing" class="progress mt-1" role="progressbar" aria-label="IDW live" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100" style="height: 10px">
                            <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: 100%">live</div>
                          </div>
                          <div v-else class="progress mt-1" role="progressbar" aria-label="IDW progress" :aria-valuenow="idw_progress" aria-valuemin="0" aria-valuemax="100" style="height: 10px">
                            <div class="progress-bar progress-bar-animated" :style="{ width: idw_progress + '%' }">{{ idw_progress}}%</div>
                          </div>
                        </li>
                       </ul>
                     </div>
                   </div>
                 </div>
                <div class="accordion-item">
                   <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                      data-bs-target="#panelsStayOpen-collapseThree-map" aria-expanded="false" aria-controls="panelsStayOpen-collapseThree-map">
                       Statistics
                    </button>
                   </h2>
                   <div id="panelsStayOpen-collapseThree-map" class="accordion-collapse collapse">
                     <div class="accordion-body">
                       <ul class="list-group list-group-flush">
                         <li class="list-group-item">
                          <table v-if="Object.keys(stat).length" class="table" w-auto>
                            <tbody>
                              <tr style="text-align: left;">
                                <th scope="row">Start date</th>
                                <td>{{this.stat.start_date}}</td>
                              </tr>
                              <tr style="text-align: left">
                                <th scope="row">End date</th>
                                <td>{{this.stat.end_date}}</td>
                              </tr>
                            </tbody>
                          </table>
                          <table v-if="Object.keys(stat).length" class="table">
                            <thead>
                              <tr>
                                <th scope="col">Value</th>
                                <th scope="col">Min</th>
                                <th scope="col">Max</th>
                                <th scope="col">AVG</th>
                                <th scope="col">RMS</th>
                                <th scope="col">Count</th>
                              </tr>
                            </thead>
                            <tbody class="table-group-divider">
                              <tr v-if="this.stat.r && this.r_vm" class="table-danger">
                                <th scope="row">{{ paramTitles[0] }}</th>
                                <td>{{this.stat.r.stats?.min != null ? this.stat.r.stats.min.toFixed(2) : ''}}</td>
                                <td>{{this.stat.r.stats?.max != null ? this.stat.r.stats.max.toFixed(2) : ''}}</td>
                                <td>{{this.stat.r.stats?.avg != null ? this.stat.r.stats.avg.toFixed(2) : ''}}</td>
                                <td>{{this.stat.r.stats?.std != null ? this.stat.r.stats.std.toFixed(2) : ''}}</td>
                                <td>{{this.stat.r.stats ? this.stat.r.count : ''}}</td>
                              </tr>
                              <tr v-if="this.stat.g && this.g_vm" class="table-success">
                                <th scope="row">{{ paramTitles[1] }}</th>
                                <td>{{this.stat.g.stats?.min != null ? this.stat.g.stats.min.toFixed(2) : ''}}</td>
                                <td>{{this.stat.g.stats?.max != null ? this.stat.g.stats.max.toFixed(2) : ''}}</td>
                                <td>{{this.stat.g.stats?.avg != null ? this.stat.g.stats.avg.toFixed(2) : ''}}</td>
                                <td>{{this.stat.g.stats?.std != null ? this.stat.g.stats.std.toFixed(2) : ''}}</td>
                                <td>{{this.stat.g.stats ? this.stat.g.count : ''}}</td>
                              </tr>
                              <tr v-if="this.stat.b && this.b_vm" class="table-primary">
                                <th scope="row">{{ paramTitles[2] }}</th>
                                <td>{{this.stat.b.stats?.min != null ? this.stat.b.stats.min.toFixed(2) : ''}}</td>
                                <td>{{this.stat.b.stats?.max != null ? this.stat.b.stats.max.toFixed(2) : ''}}</td>
                                <td>{{this.stat.b.stats?.avg != null ? this.stat.b.stats.avg.toFixed(2) : ''}}</td>
                                <td>{{this.stat.b.stats?.std != null ? this.stat.b.stats.std.toFixed(2) : ''}}</td>
                                <td>{{this.stat.b.stats ? this.stat.b.count : ''}}</td>
                              </tr>
                            </tbody>
                          </table>
                        </li>
                       </ul>
                     </div>
                   </div>
                 </div>
                <div class="accordion-item">
                   <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                      data-bs-target="#panelsStayOpen-collapseFour-map" aria-expanded="false" aria-controls="panelsStayOpen-collapseFour-map">
                       Layers
                    </button>
                   </h2>
                   <div id="panelsStayOpen-collapseFour-map" class="accordion-collapse collapse">
                     <div class="accordion-body">
                       <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1 mb-1">
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="switchTrack-map" v-model="isTrackOn">
                                <label class="form-check-label" for="switchTrack-map">Track</label>
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="switchAnimation-map" v-model="isAnimationOn" :disabled="!isTrackOn">
                                <label class="form-check-label" for="switchAnimation-map">Animation</label>
                              </div>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1 mb-1">
                            <div class="col">
                              <!-- <div class="form-check form-switch mt-1 form-switch">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault0-map" v-model="heatmap_vm" :disabled="isLoadingData">
                                <label class="form-check-label" for="flexSwitchCheckDefault0-map">Points</label>
                              </div> -->
                              <!-- <button class="btn btn-outline-primary" :class=" heatmap_vm ? 'active' : ''"  @click="heatmap_vm = !heatmap_vm" data-bs-toggle="button" autocomplete="off">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#pin-map"/>
                                </svg>
                                Points
                              </button> -->
                              <div class="form-check form-switch mt-1">
                                 <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefaultPoints-map" v-model="heatmap_vm" :disabled="!is_ssc">
                                 <label class="form-check-label" for="flexSwitchCheckDefaultPoints-map">Points</label>
                              </div>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary"  @click="is_fit_vm = !is_fit_vm" data-bs-toggle="button" autocomplete="off">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#crosshair"/>
                                </svg>
                                Fit
                              </button>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-2 mt-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#arrows-angle-expand"/>
                                </svg>
                                Point size
                              </span>
                              <input type="number" class="form-control" v-model="hmPointSize" :disabled="isDrawing">
                            </div>
                          </div>
                         </li>
                         <li class="list-group-item">
                           <div class="row row-cols-auto">
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault1-map" v-model="idw_vm">
                                <label class="form-check-label" for="flexSwitchCheckDefault1-map">Visualisation</label>
                              </div>
                            </div>
                            <div class="col">
                              <!-- <label for="customRange1-map" class="form-label">Opasity</label> -->
                              <input type="range" class="form-range mt-1" id="customRange1-map" min="0" max="1" step="0.01" v-model="idw_opacity">
                            </div>
                            <div class="col">
                               <div class="form-check form-switch mt-1">
                                 <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault6-map" v-model="is_water_mask" :disabled="!is_ssc" @change="handleIsWaterMask">
                                 <label class="form-check-label" for="flexSwitchCheckDefault6-map">Water mask</label>
                               </div>
                             </div>
                             <div class="col">
                               <button type="button" class="btn btn-outline-primary" @click="openAddWaterMask">
                                 <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/></svg>
                                 Add new
                               </button>
<!--                                <button
                                 type="button"
                                 class="btn btn-outline-secondary btn-sm mt-1"
                                 :disabled="!editableSelectedArea"
                                 :title="editableSelectedArea ? 'Edit selected water mask' : 'Select an UI-created mask first'"
                                 @click="openEditWaterMask"
                               >
                                 Edit
                               </button> -->
                             </div>
                             <div class="col">
                                <button class="btn btn-outline-primary" @click="openEditWaterMask" :title="areas && areas.length ? 'Manage water masks' : 'No areas yet'">
                                  <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#gear"/></svg>
                                  Edit
                                </button>
                             </div>
                           </div>
<!--                            <div class="row mb-1">
                            <label for="colFormLabelSm-map" class="col-sm-3 col-form-label col-form-label-sm">
                              <svg class="bi" width="16" height="16" fill="currentColor">
                                <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#pin-map-fill"/>
                              </svg>
                              Area
                            </label>
                            <div class="col-sm-9">
                              <select class="form-select form-select-sm" aria-label="Default select example" id="colFormLabelSm-map" placeholder="Choose area..." v-model="selected_area_name" @change="handleAreaChange">
                                <option v-for="area in areas" :value="area.name" :key="area">
                                  {{ area.name }}
                                </option>
                              </select>
                            </div>
                          </div> -->
                         </li>
                         <li class="list-group-item">
                           <div class="row row-cols-auto">
                             <div class="col">
                               <div class="form-check form-switch mt-1">
                                 <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault8-map" v-model="is_bathmetry" :disabled="is_local">
                                 <label class="form-check-label" for="flexSwitchCheckDefault8-map">Bathmetry (GEBCO WMS)</label>
                               </div>
                             </div>
                           </div>
                         </li>
                         <!-- <li class="list-group-item">
                           <div class="row row-cols-auto">
                             <div class="col">
                               <div class="form-check form-switch mt-1">
                                 <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault7-map" v-model="is_ssc">
                                 <label class="form-check-label" for="flexSwitchCheckDefault7-map">server-side calculation</label>
                               </div>
                             </div>
                           </div>
                         </li> -->
                       </ul>
                     </div>
                   </div>
                 </div>
              </div>
            </div>
            <div v-show="currentRouteName=='profiling'" class="offcanvas-body">
              <div class="accordion" id="accordionPanelsStayOpenExample-profiling">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne-profiling"
                     aria-expanded="true" aria-controls="panelsStayOpen-collapseOne-profiling">
                      Data
                    </button>
                  </h2>
                  <div id="panelsStayOpen-collapseOne-profiling" class="accordion-collapse collapse show">
                    <div class="accordion-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row mb-1">
                            <VueDatePicker id="manual-profiling" :key="datePickerKey" :model-value="date" enable-seconds range :timezone="{ convertModel: true, timezone: 'UTC' }" @update:model-value="setDate" style="--dp-font-size: 0.85rem" :disabled="isLoadingData || isDrawing"/>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col">
                              <div class="btn-group" role="group" aria-label="Basic radio toggle button group" >
                                <input type="radio" class="btn-check" name="btnradio-profiling" id="btnradio1-profiling" autocomplete="off" @click="set_custom()" :checked="is_custom_data" :disabled="isLoadingData  || isDrawing">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio1-profiling">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#activity"/>
                                  </svg>
                                  Custom
                                </label>
                                <input type="radio" class="btn-check" name="btnradio-profiling" id="btnradio2-profiling" autocomplete="off" @click="set_complete()" :checked="!is_custom_data" :disabled="isLoadingData  || isDrawing">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio2-profiling">
                                  <span v-if="isLoadingData && isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                  <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#database"/>
                                  </svg>
                                  Complete
                                </label>
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchIsLive-profiling" v-model="is_live" :disabled="!is_ssc">
                                <label class="form-check-label" for="flexSwitchIsLive-profiling">live</label>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col-sm-4">
                              <label for="colFormSiteLaser-profiling" class="col-form-label-sm">
                                Site
                              </label>
                            </div>
                            <div class="col-sm-8">
                              <select class="form-select form-select-sm" aria-label="Default select example" id="colFormSiteLaser-profiling" placeholder="Choose site..." v-model="selected_area_name" @change="handleAreaChange">
                                <option selected>Choose site...</option>
                                <option v-for="area in areas" :value="area.name" :key="area">
                                  {{ area.name }}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col-sm-4">
                              <label for="colFormLabelLaser-profiling" class="col-form-label-sm">
                                Laser, nm
                              </label>
                            </div>
                            <div class="col-sm-8">
                              <select class="form-select form-select-sm" id="colFormLabelLaser-profiling" v-model="laser">
                                <option v-for="laser in lasers" :value="laser" :key="laser">
                                  {{ laser }}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div v-if="is_live" class="row row-cols-auto mb-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#clock-history"/>
                                </svg>
                                Interval, s
                              </span>
                              <input type="number" class="form-control" min="0" v-model="intervalTime" :disabled="isDrawing">
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 align-items-center">
                            <div class="col">
                              <div class="input-group input-group-sm">
                                <span class="input-group-text">
                                  <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#clock"/>
                                  </svg>
                                  Average, s
                                </span>
                                <input type="number" class="form-control" min="0" step="1" v-model.number="profilingAvgTime" :disabled="isDrawing">
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchLog-profiling" v-model="profilingLogScale" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchLog-profiling">log_10 scale</label>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button @click="profilingButtonAction()" class="btn" :class="profilingLiveActive ? 'btn-outline-danger' : 'btn-outline-primary'" :disabled="isLoadingData || isDrawing">
                                <span v-if="isLoadingData && !isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <svg v-else-if="profilingLiveActive" class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-circle"/>
                                </svg>
                                <svg v-else-if="is_live" class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#check2-square"/>
                                </svg>
                                <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#search"/>
                                </svg>
                                {{ profilingBtnText }}
                              </button>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                     data-bs-target="#panelsStayOpen-collapseFour-profiling" aria-expanded="false" aria-controls="panelsStayOpen-collapseFour-profiling">
                      Layers
                    </button>
                  </h2>
                  <div id="panelsStayOpen-collapseFour-profiling" class="accordion-collapse collapse">
                    <div class="accordion-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1 mb-1">
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="switchTrack-profiling" v-model="isTrackOn">
                                <label class="form-check-label" for="switchTrack-profiling">Track</label>
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="switchAnimation-profiling" v-model="isAnimationOn" :disabled="!isTrackOn">
                                <label class="form-check-label" for="switchAnimation-profiling">Animation</label>
                              </div>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1 mb-1">
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefaultPoints-profiling" v-model="profilingPointsOn">
                                <label class="form-check-label" for="flexSwitchCheckDefaultPoints-profiling">Points</label>
                              </div>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="profilingFitOn = !profilingFitOn" data-bs-toggle="button" autocomplete="off">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#crosshair"/>
                                </svg>
                                Fit
                              </button>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-2 mt-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#arrows-angle-expand"/>
                                </svg>
                                Point size
                              </span>
                              <input type="number" class="form-control" min="1" v-model.number="profilingPointSize" :disabled="isDrawing">
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto">
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault6-profiling" v-model="is_water_mask" :disabled="!is_ssc" @change="handleIsWaterMask">
                                <label class="form-check-label" for="flexSwitchCheckDefault6-profiling">Water mask</label>
                              </div>
                            </div>
                            <div class="col">
                              <button type="button" class="btn btn-outline-primary" @click="openAddWaterMask">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/></svg>
                                Add new
                              </button>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="openEditWaterMask" :title="areas && areas.length ? 'Manage water masks' : 'No areas yet'">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#gear"/></svg>
                                Edit
                              </button>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto">
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault8-profiling" v-model="is_bathmetry" :disabled="is_local">
                                <label class="form-check-label" for="flexSwitchCheckDefault8-profiling">Bathmetry (GEBCO WMS)</label>
                              </div>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-show="currentRouteName=='fluo'" class="offcanvas-body">
              <div class="accordion" id="accordionPanelsStayOpenExample-fluo">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne-fluo"
                     aria-expanded="true" aria-controls="panelsStayOpen-collapseOne-fluo">
                      Data
                    </button>
                  </h2>
                  <div id="panelsStayOpen-collapseOne-fluo" class="accordion-collapse collapse show">
                    <div class="accordion-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row mb-1">
                            <VueDatePicker id="manual-fluo" :key="datePickerKey" :model-value="date" enable-seconds range :timezone="{ convertModel: true, timezone: 'UTC' }" @update:model-value="setDate" style="--dp-font-size: 0.85rem" :disabled="isLoadingData || isDrawing"/>
                            <!-- <VueDatePicker id="manual-fluo" :model-value="date" enable-seconds :format="format" range :timezone="{ convertModel: true, timezone: 'UTC' }" @update:model-value="setDate" e/> -->
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col">
                              <div class="btn-group" role="group" aria-label="Basic radio toggle button group" >
                                <input type="radio" class="btn-check" name="btnradio-fluo" id="btnradio1-fluo" autocomplete="off" @click="set_custom()" :checked="is_custom_data" :disabled="isLoadingData  || isDrawing">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio1-fluo">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#activity"/>
                                  </svg>
                                  Custom
                                </label>
                                <input type="radio" class="btn-check" name="btnradio-fluo" id="btnradio2-fluo" autocomplete="off" @click="set_complete()" :checked="!is_custom_data" :disabled="isLoadingData  || isDrawing">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio2-fluo">
                                  <span v-if="isLoadingData && isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                  <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#database"/>
                                  </svg>
                                  Complete
                                </label>
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchIsLive-fluo" v-model="is_live" :disabled="!is_ssc">
                                <label class="form-check-label" for="flexSwitchIsLive-fluo">live</label>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col-sm-4">
                              <label for="colFormLabelLaser-fluo" class="col-form-label-sm">
                                Site
                              </label>
                            </div>
                            <div class="col-sm-8">
                              <select class="form-select form-select-sm" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose site..." v-model="selected_area_name" @change="handleAreaChange" :disabled="!is_water_mask">
                                <option selected>Choose site...</option>
                                <option v-for="area in areas" :value="area.name" :key="area">
                                  {{ area.name }}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col-sm-4">
                              <label for="colFormLabelLaser-fluo" class="col-form-label-sm">
                                Laser, nm
                              </label>
                            </div>
                            <div class="col-sm-8">
                              <select class="form-select form-select-sm" id="colFormLabelLaser-fluo" v-model="laser">
                                <option v-for="laser in lasers" :value="laser" :key="laser">
                                  {{ laser }}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div v-if="is_live" class="row row-cols-auto mb-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#clock-history"/>
                                </svg>
                                Interval, s
                              </span>
                              <input type="number" class="form-control" min="0" v-model="intervalTime" :disabled="isDrawing">
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button @click="get_params()" class="btn btn-outline-primary" :disabled="isLoadingData || isDrawing">
                                <span v-if="isLoadingData && !isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#search"/>
                                </svg>
                                <!-- <span role="status">Loading...</span> -->
                                Search
                              </button>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                     data-bs-target="#panelsStayOpen-collapseTwo-fluo" aria-expanded="false" aria-controls="panelsStayOpen-collapseTwo-fluo">
                      Calibrations
                    </button>
                  </h2>
                  <div id="panelsStayOpen-collapseTwo-fluo" class="accordion-collapse collapse" :class="(mins1.length && mins2.length) ? 'show' : ''">
                    <div class="accordion-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="openDescriptionModal">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#table"/></svg>
                                Calibration Curve
                              </button>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="openAddCalibrationModal">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/></svg>
                                Add new
                              </button>
                            </div>
                          </div>
                          <div class="row row-cols-auto mt-1">
                            <div class="col">
                              <div class="form-check form-switch switch-red mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault2-fluo" v-model="r_vm" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchCheckDefault2-fluo">{{ paramTitles[0] }}</label>
                              </div>
                            </div>
                          </div>
                          <!-- <div v-if="r_vm" class="row g-0 align-items-center mb-2">
                              <div class="col">
                                <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_cal_r" :disabled="!cals.length || isDrawing" @change="min_changed('', '')">
                                  <option v-for="cal in cals" :value="cal" :key="cal">
                                    {{ cal }}
                                  </option>
                                </select>
                              </div>
                              <div class="col-auto divider">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                                </svg>
                              </div>
                              <div class="col">
                                  <div class="row g-0 mb-1">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_min1_r" :disabled="!mins1_r.length || isDrawing" @change="min_changed(selected_min1_r, 'r1')">
                                          <option v-for="min in mins1_r" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                                  <div class="row g-0">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_min2_r" :disabled="!mins2_r.length || isDrawing" @change="min_changed(selected_min2_r, 'r2')">
                                          <option v-for="min in mins2_r" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                              </div>
                          </div> -->
                          <div v-if="r_vm">
                            <div class="row row-cols-auto mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm1-fluo" class="col-form-label-sm">
                                  Signal
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm1-fluo" v-model="selectedSigR" @change="onSignalChanged('r')">
                                  <option v-for="sig in signals" :value="sig" :key="sig">
                                    {{ sig }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm2-fluo" class="col-form-label-sm">
                                  Normalization
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm2-fluo" v-model="selectedNormR" :disabled="!selectedSigR || loadingNormsR">
                                  <option value="No">No</option>
                                  <option v-for="norm in compatibleNormsR" :value="norm" :key="norm">
                                    {{ norm }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm3-fluo" class="col-form-label-sm">
                                  Calibration Curve
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" v-model="selectedCalRId" id="colFormLabelSm3-fluo" :key="calibrations.length">
                                  <option value="">No</option>
                                  <option v-for="cal in calibrations" :key="cal.id" :value="cal.id">
                                    {{ cal.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1">
                            <div class="col">
                              <div class="form-check form-switch switch-green mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault3-fluo" v-model="g_vm" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchCheckDefault3-fluo">{{ paramTitles[1] }}</label>
                              </div>
                            </div>
                          </div>
                          <!-- <div v-if="g_vm" class="row g-0 align-items-center mb-2">
                              <div class="col">
                                <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_cal_g" :disabled="!cals.length || isDrawing"  @change="min_changed('', '')">
                                  <option v-for="cal in cals" :value="cal" :key="cal">
                                    {{ cal }}
                                  </option>
                                </select>
                              </div>
                              <div class="col-auto divider">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                                </svg>
                              </div>
                              <div class="col">
                                  <div class="row g-0 mb-1">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_min1_g" :disabled="!mins1_g.length || isDrawing" @change="min_changed(selected_min1_g, 'g1')">
                                          <option v-for="min in mins1_g" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                                  <div class="row g-0">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_min2_g" :disabled="!mins2_g.length || isDrawing" @change="min_changed(selected_min2_g, 'g2')">
                                          <option v-for="min in mins2_g" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                              </div>
                          </div> -->
                          <div v-if="g_vm">
                            <div class="row row-cols-auto mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm10-fluo" class="col-form-label-sm">
                                  Signal
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm10-fluo" v-model="selectedSigG" @change="onSignalChanged('g')">
                                  <option v-for="sig in signals" :value="sig" :key="sig">
                                    {{ sig }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm11-fluo" class="col-form-label-sm">
                                  Normalization
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm11-fluo" v-model="selectedNormG" :disabled="!selectedSigG || loadingNormsG">
                                  <option value="No">No</option>
                                  <option v-for="norm in compatibleNormsG" :value="norm" :key="norm">
                                    {{ norm }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm12-fluo" class="col-form-label-sm">
                                  Calibration Curve
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" v-model="selectedCalGId" id="colFormLabelSm12-fluo" :key="calibrations.length">
                                  <option value="">No</option>
                                  <option v-for="cal in calibrations" :key="cal.id" :value="cal.id">
                                    {{ cal.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="row row-cols-auto mt-1">
                            <div class="col">
                              <div class="form-check form-switch switch-blue mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault4-fluo" v-model="b_vm" :disabled="isDrawing">
                                <label class="form-check-label" for="flexSwitchCheckDefault4-fluo">{{ paramTitles[2] }}</label>
                              </div>
                            </div>
                          </div>
                          <!-- <div v-if="b_vm" class="row g-0 align-items-center mb-2">
                              <div class="col">
                                <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_cal_b" :disabled="!cals.length || isDrawing"  @change="min_changed('', '')">
                                  <option v-for="cal in cals" :value="cal" :key="cal">
                                    {{ cal }}
                                  </option>
                                </select>
                              </div>
                              <div class="col-auto divider">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                                </svg>
                              </div>
                              <div class="col">
                                  <div class="row g-0 mb-1">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_min1_b" :disabled="!mins1_b.length || isDrawing" @change="min_changed(selected_min1_b, 'b1')">
                                          <option v-for="min in mins1_b" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                                  <div class="row g-0">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-fluo" placeholder="Choose area..." v-model="selected_min2_b" :disabled="!mins2_b.length || isDrawing" @change="min_changed(selected_min2_b, 'b2')">
                                          <option v-for="min in mins2_b" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                              </div>
                          </div> -->
                          <div v-if="b_vm">
                            <div class="row row-cols-auto mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm14-fluo" class="col-form-label-sm">
                                  Signal
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm14-fluo" v-model="selectedSigB" @change="onSignalChanged('b')">
                                  <option v-for="sig in signals" :value="sig" :key="sig">
                                    {{ sig }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm15-fluo" class="col-form-label-sm">
                                  Normalization
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm15-fluo" v-model="selectedNormB" :disabled="!selectedSigB || loadingNormsB">
                                  <option value="No">No</option>
                                  <option v-for="norm in compatibleNormsB" :value="norm" :key="norm">
                                    {{ norm }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm16-fluo" class="col-form-label-sm">
                                  Calibration Curve
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" v-model="selectedCalBId" id="colFormLabelSm16-fluo" :key="calibrations.length">
                                  <option value="">No</option>
                                  <option v-for="cal in calibrations" :key="cal.id" :value="cal.id">
                                    {{ cal.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button class="btn btn-outline-primary">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/></svg>
                                Add Measuring Parameter
                              </button>
                            </div>
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="openEditModal">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#gear"/></svg>
                                Edit
                              </button>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 mt-1">
                            <div class="col">
                              <button class="btn btn-outline-primary" @click="load_fluo">
                                <svg class="bi" width="16" height="16"><use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/></svg>
                                Download Fluo Spectrum
                              </button>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-show="currentRouteName=='charts'" class="offcanvas-body">
              <div class="accordion" id="accordionPanelsStayOpenExample-charts">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseFour-charts"
                     aria-expanded="true" aria-controls="panelsStayOpen-collapseFour-charts">
                      Data
                    </button>
                  </h2>
                  <div id="panelsStayOpen-collapseFour-charts" class="accordion-collapse collapse show">
                    <div class="accordion-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="row mb-1">
                            <VueDatePicker id="manual-charts" :key="datePickerKey" :model-value="date" enable-seconds range :timezone="{ convertModel: true, timezone: 'UTC' }" @update:model-value="setDate" style="--dp-font-size: 0.85rem" :disabled="isLoadingData"/>
                            <!-- <VueDatePicker id="manual-charts" :model-value="date" enable-seconds :format="format" range :timezone="{ convertModel: true, timezone: 'UTC' }" @update:model-value="setDate" e/> -->
                          </div>
                          <div class="row row-cols-auto mb-1">
                            <div class="col">
                              <div class="btn-group" role="group" aria-label="Basic radio toggle button group" >
                                <input type="radio" class="btn-check" name="btnradio-charts" id="btnradio1-charts" autocomplete="off" @click="set_custom()" :checked="is_custom_data" :disabled="isLoadingData">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio1-charts">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#activity"/>
                                  </svg>
                                  Custom
                                </label>
                                <input type="radio" class="btn-check" name="btnradio-charts" id="btnradio2-charts" autocomplete="off" @click="set_complete()" :checked="!is_custom_data" :disabled="isLoadingData">
                                <label class="btn btn-outline-primary btn-sm" for="btnradio2-charts">
                                  <span v-if="isLoadingData && isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                  <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#database"/>
                                  </svg>
                                  Complete
                                </label>
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch mt-1">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchIsLive-charts" v-model="is_live" :disabled="!is_ssc">
                                <label class="form-check-label" for="flexSwitchIsLive-charts">live</label>
                              </div>
                            </div>
                          </div>
                          <div v-if="is_live" class="row row-cols-auto mb-1">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text">
                                <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#clock-history"/>
                                </svg>
                                Interval, s
                              </span>
                              <input type="number" class="form-control" min="0" v-model="intervalTime" :disabled="isDrawing">
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-1 align-items-center">
                            <div class="col">
                              <div class="input-group input-group-sm">
                                <span class="input-group-text">
                                  <svg class="bi" width="16" height="16" fill="currentColor" style="margin-right : 0.5rem">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#calculator"/>
                                  </svg>
                                  Average, s
                                </span>
                                <input type="number" class="form-control" min="0" v-model="avgTime" :disabled="isLoadingData  || isDrawing">
                              </div>
                            </div>
                            <div class="col">
                              <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchLog-charts" v-model="log_vm" :disabled="isLoadingData || isDrawing">
                                <label class="form-check-label" for="flexSwitchLog-charts">log_10 scale</label>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto">
<!--                             <div class="col">
                              <button @click="get_params ()" class="btn btn-outline-primary" :disabled="isLoadingData">
                                <span v-if="isLoadingData && !isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#search"/>
                                </svg>
                                Search
                              </button>
                            </div> -->
                            <!-- <div class="col">
                              <button class="btn btn-outline-primary"  @click="isInfo = !isInfo" data-bs-toggle="button" autocomplete="off" :disabled="isLoadingData || !stat">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#info-circle"/>
                                </svg>
                                Info
                              </button>
                            </div> -->
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                   <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                      data-bs-target="#panelsStayOpen-collapseFive-charts" aria-expanded="false" aria-controls="panelsStayOpen-collapseFive-charts">
                       Calibrations
                    </button>
                   </h2>
                   <div id="panelsStayOpen-collapseFive-charts" class="accordion-collapse collapse show">
                     <div class="accordion-body">
                       <ul class="list-group list-group-flush">
                         <li class="list-group-item">
                            <!-- <div class="row g-0 align-items-center mb-2 no-gutters">
                              <div class="col">
                                <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-charts" placeholder="Choose area..." v-model="selected_cal" :disabled="!cals.length">
                                  <option v-for="cal in cals" :value="cal" :key="cal">
                                    {{ cal }}
                                  </option>
                                </select>
                              </div>
                              <div class="col-auto divider">
                                <svg class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                                </svg>
                              </div>
                              <div class="col">
                                  <div class="row g-0 mb-1 no-gutters">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-charts" placeholder="Choose area..." v-model="selected_min1" :disabled="!mins1.length" @change="min_changed(selected_min1, 'c1')">
                                          <option v-for="min in mins1" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                                  <div class="row g-0 no-gutters">
                                      <div class="col">
                                        <select class="form-select form-select-sm compact-select" aria-label="Default select example" id="colFormLabelSm-charts" placeholder="Choose area..." v-model="selected_min2" :disabled="!mins2.length" @change="min_changed(selected_min2, 'c2')">
                                          <option v-for="min in mins2" :value="min" :key="min">
                                            {{ min }}
                                          </option>
                                        </select>
                                      </div>
                                  </div>
                              </div>
                              <div class="col-auto">
                                <input type="color" class="form-control form-control-color" id="exampleColorInput-charts" value="#563d7c" title="Choose your color" v-model="chartColor">
                              </div>
                          </div> -->
                          <div>
                            <div class="row row-cols-auto mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm1-charts" class="col-form-label-sm">
                                  Signal
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm1-charts" v-model="activeChartsSignal" @change="onChartsSignalChanged">
                                  <option v-for="sig in signals" :value="sig" :key="sig">
                                    {{ sig }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm2-charts" class="col-form-label-sm">
                                  Normalization
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" id="colFormLabelSm2-charts" v-model="activeChartsNormalization" :disabled="chartsNormalizationDisabled">
                                  <option value="No">No</option>
                                  <option v-for="norm in chartsCompatibleNorms" :value="norm" :key="norm">
                                    {{ norm }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="row mb-1">
                              <div class="col-sm-4">
                                <label for="colFormLabelSm3-charts" class="col-form-label-sm">
                                  Calibration Curve
                                </label>
                              </div>
                              <div class="col-sm-8">
                                <select class="form-select form-select-sm" v-model="activeChartsCalibrationId" id="colFormLabelSm3-charts" :key="calibrations.length">
                                  <option value="">No</option>
                                  <option v-for="cal in calibrations" :key="cal.id" :value="cal.id">
                                    {{ cal.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <div class="row row-cols-auto mb-2">
                            <div class="col">
                              <button class="btn btn-outline-success"  @click="add_chart()" autocomplete="off" :disabled="isLoadingData">
                                <span v-if="isLoadingData && !isLoadingTimerange" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <svg v-else class="bi" width="16" height="16" fill="currentColor">
                                  <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#plus-circle"/>
                                </svg>
                                Add chart
                              </button>
                            </div>
                            <div class="col-auto">
                                <input type="color" class="form-control form-control-color" id="exampleColorInput-charts" value="#563d7c" title="Choose your color" v-model="chartColor">
                              </div>
                          </div>

                          </li>
                           <div v-if="charts.size > 0"></div>
                           <div 
                            v-for="chart in [...charts].reverse()"
                            :key="chart"
                            class="list-group-item"
                           >
                            <div class="row g-0 align-items-center mb-2 no-gutters">
                              <div class="col-auto">
                                <button @click="remove_chart(chart)" class="btn btn-outline-danger" autocomplete="off" :disabled="isLoadingData || !result">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#dash-circle"/>
                                  </svg>
                                </button>
                              </div>
                              <div class="col-auto mt-3 divider">
                                <!-- <p><small>{{chart.type}}</small></p> -->
                                <div class="alert alert-primary small-alert" role="alert">{{chart.type}}</div>
                               </div>
                              <div class="col-auto">
                                <input type="color" class="form-control form-control-color" :value="chart.color" title="Choose your color" @change="update_chart_color(chart, $event.target.value)">
                              </div>
                            </div>
                            <div class="row g-0 align-items-center mb-2 no-gutters">
                              <div class="col mt-1">
                                <div class="input-group input-group-sm">
                                  <div class="input-group-text">
                                    <label class="form-check-label" style="margin-right : 1rem">Data limits</label>
                                    <input class="form-check-input mt-0" type="checkbox" v-model="chart.is_filter">
                                  </div>
                                    <input type="number" class="form-control" placeholder="min" min="0" step="0.01" :disabled="!chart.is_filter" v-model="chart.filter_min">
                                    <input type="number" class="form-control" placeholder="max" step="0.01" :disabled="!chart.is_filter" v-model="chart.filter_max">
                                   <div class="input-group-text">
                                    <button @click="apply_chart_filter(chart)" class="btn btn-outline-primary btn-sm" autocomplete="off" :disabled="isLoadingData || !result">
                                      <svg class="bi" width="16" height="16" fill="currentColor">
                                        <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#check2-square"/>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-for="param in [...chartsEnabledParameters].reverse()"
                            :key="'auto-' + param.id"
                            class="list-group-item"
                          >
                            <div class="row g-0 align-items-center mb-2 no-gutters">
                              <div class="col-auto">
                                <button @click="remove_auto_chart(param.id)" class="btn btn-outline-danger" autocomplete="off" :disabled="isLoadingData">
                                  <svg class="bi" width="16" height="16" fill="currentColor">
                                    <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#dash-circle"/>
                                  </svg>
                                </button>
                              </div>
                              <div class="col-auto mt-3 divider">
                                <div class="alert alert-primary small-alert" role="alert">{{ param.label || param.title }}</div>
                              </div>
                              <div class="col-auto">
                                <input type="color" class="form-control form-control-color" :value="param.color" title="Choose your color" @change="update_auto_chart_color(param.id, $event.target.value)">
                              </div>
                            </div>
                            <div class="row g-0 align-items-center mb-2 no-gutters">
                              <div class="col mt-1">
                                <div class="input-group input-group-sm">
                                  <div class="input-group-text">
                                    <label class="form-check-label" style="margin-right : 1rem">Data limits</label>
                                    <input
                                      class="form-check-input mt-0"
                                      type="checkbox"
                                      :checked="param.is_filter"
                                      @change="set_auto_chart_field(param.id, 'is_filter', $event.target.checked)"
                                    >
                                  </div>
                                    <input type="number" class="form-control" placeholder="min" min="0" step="0.01"
                                      :disabled="!param.is_filter"
                                      :value="param.filter_min"
                                      @input="set_auto_chart_field(param.id, 'filter_min', $event.target.value === '' ? null : Number($event.target.value))">
                                    <input type="number" class="form-control" placeholder="max" step="0.01"
                                      :disabled="!param.is_filter"
                                      :value="param.filter_max"
                                      @input="set_auto_chart_field(param.id, 'filter_max', $event.target.value === '' ? null : Number($event.target.value))">
                                   <div class="input-group-text">
                                    <button @click="apply_auto_chart_filter(param.id)" class="btn btn-outline-primary btn-sm" autocomplete="off" :disabled="isLoadingData">
                                      <svg class="bi" width="16" height="16" fill="currentColor">
                                        <use xlink:href="~/bootstrap-icons/bootstrap-icons.svg#check2-square"/>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                       </ul>
                     </div>
                   </div>
                 </div>
                <!-- <div class="accordion-item">
                   <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                      data-bs-target="#panelsStayOpen-collapseSix-charts" aria-expanded="false" aria-controls="panelsStayOpen-collapseSix-charts">
                       Settings
                    </button>
                   </h2>
                   <div id="panelsStayOpen-collapseSix-charts" class="accordion-collapse collapse show">
                   </div>
                 </div> -->
              </div>
            </div>
    </div>
    <div class="d-flex flex-column bg-light" style="flex: 1 1 0; min-width: 0; overflow: hidden;">
      <router-view v-slot="{ Component }">
        <keep-alive :exclude="['SignIn', 'ProfileView', 'AdminView']">
          <component :is="Component" @statusDrawing2='processDrawing' @paletteChange2='paletteChange'/>
        </keep-alive>
      </router-view>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, provide, reactive, nextTick } from 'vue';
import { mapActions, mapGetters, useStore } from 'vuex';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import axios from 'axios';
import ColorPalette from './ColorPalette'
import CalibrationFormModal from './CalibrationFormModal';
import CalibrationTableModal from './CalibrationTableModal';
import EditParameterNamesModal from './EditParameterNamesModal';
import AddWaterMaskModal from './AddWaterMaskModal';
import PrintReportModal from './PrintReportModal';

export default defineComponent({
  name: 'NavBar',
  setup () {
    const stat = ref({});
    const selectedCalRId = ref();
    const selectedSigR = ref();
    const selectedNormR = ref();
    const selectedCalGId = ref();
    const selectedSigG = ref();
    const selectedNormG = ref();
    const selectedCalBId = ref();
    const selectedSigB = ref();
    const selectedNormB = ref();
    // signals/norms формируются динамически из ответа measurements/params
    // (поле all_params) в get_params(). До загрузки данных доступен только
    // вариант "No".
    const signals = ref(["No"]);
    const norms = ref(["No"]);
    const lasers = ref(["", "266", "355", "532", "355+532"]);
    const isTrackOn = ref(true);
    const isAnimationOn = ref(false);
    const profilingAvgTime = ref(0);
    const profilingPointsOn = ref(true);
    const profilingFitOn = ref(false);
    const profilingPointSize = ref(5);
    const isInfo = ref(false);
    const paletteWrapper = ref(null);
    const date = ref();
    // Бампим при ПРОГРАММНОМ изменении диапазона (снапы live/Search), чтобы
    // принудительно перемонтировать VueDatePicker: в контролируемом режиме
    // (:model-value) он не всегда переунифицирует внутреннее состояние из
    // model-value, и правая граница не отрисовывалась. Ремоунт заставляет его
    // прочитать актуальный date заново. Пользовательский выбор key не трогает.
    const datePickerKey = ref(0);
    const store = useStore();
    const tableDate = ref();
    const result = ref({data: [[]]});
    const isLoadingTimerange = ref(false);
    const idwAction = ref('');
    const isPalette = ref(false);
    const switchPalette = ref(false);
    const isDrawing = ref(false);
    const isStarted = ref(false);
    const isOffcanvas = ref(true);
    const offcanvasWidth = ref(400);
    const isOffcanvasLocked = ref(false);
    const offcanvasStateBeforeLock = ref(true);
    const toggleOptionPanel = () => {
      if (isOffcanvasLocked.value) return;
      isOffcanvas.value = !isOffcanvas.value;
      nextTick(() => window.dispatchEvent(new Event('resize')));
    };
    const is_local = ref(false);
    const is_bathmetry = ref(false);
    const is_live = ref(false);
    const selected_chart = ref(0);
    const chartColor = ref('#f87979');
    const newChart = ref({});
    const colorChart = ref({ id: null, color: null, ts: 0 });
    const selectedPoint = ref({ timestamp: null, source: null, ts: 0 });
    const chartsTabSig = ref('');
    const chartsTabNorm = ref('');
    const chartsTabCalId = ref('');
    const filterChart = reactive({
      id: null,
      type: null,
      color: null,
      is_filter: false,
      filter_min: 0,
      filter_max: 100
    });
    const charts = ref(new Set());
    const chart_labels = {'cdom' : 'Min(440_1,1)', 'chla' : 'Chl-a, mg/L', 'tss' : 'TSS, mg/L'}
    const idwText = ref("Apply");
    // Кнопка Search на вкладке Profiling в live-режиме превращается в Apply/Cancel
    // (по аналогии с Map). profilingLiveActive — идёт ли сейчас live-опрос профайлинга.
    const profilingLiveActive = ref(false);
    const idw_progress = ref(0);
    const areas = ref([]);
    const selected_area_name = ref("Choose site...");
    const selected_area = ref(0);
    const mins1 = ref([]);
    const mins2 = ref([]);
    const mins1_r = ref([]);
    const mins2_r = ref([]);
    const mins1_g = ref([]);
    const mins2_g = ref([]);
    const mins1_b = ref([]);
    const mins2_b = ref([]);
    const cals = ref([]);
    // Соответствие человекочитаемого названия Signal/Normalization -> MIN-строка.
    // Заполняется из поля all_params ответа measurements/params, чтобы не
    // парсить MIN из текста названия.
    const paramMinByLabel = ref({});
    const selected_min1 = ref();
    const selected_min2 = ref();
    const selected_cal = ref();
    const paletteInfo = ref();
    const activePalette = ref();
    const selected_min1_r = ref();
    const selected_min2_r = ref();
    const selected_cal_r = ref();
    const selected_min1_g = ref();
    const selected_min2_g = ref();
    const selected_cal_g = ref();
    const selected_min1_b = ref();
    const selected_min2_b = ref();
    const selected_cal_b = ref();
    const formulae_result1 = ref('');
    const formulae_result2 = ref('');
    const formulae_result3 = ref('');
    const compatibleNormsR = ref([]);
    const compatibleNormsG = ref([]);
    const compatibleNormsB = ref([]);
    const loadingNormsR = ref(false);
    const loadingNormsG = ref(false);
    const loadingNormsB = ref(false);
    const compatibleNormsCharts = ref([]);
    const loadingNormsCharts = ref(false);
    // Хранилище MIN-строк, реально совместимых с выбранным Signal по каналу/времени/полигону.
    // Используется в draw(), чтобы Apply резолвил имя нормализации в MIN из ограниченного
    // (по каналу) набора, а не из глобального all_mins — иначе name "650 X" мог разрешаться
    // в MIN того же канала, что и Signal, и формула получалась противоречивой.
    const compatibleNormMinsR = ref([]);
    const compatibleNormMinsG = ref([]);
    const compatibleNormMinsB = ref([]);
    const compatibleNormMinsCharts = ref([]);
    const redPaletteConfig = reactive({
      baseColor: '#b33e23',
      numColors: 14,
      usePalette: true
    });
    
    const greenPaletteConfig = reactive({
      baseColor: '#2a5620',
      numColors: 14,
      usePalette: true
    });
    
    const bluePaletteConfig = reactive({
      baseColor: '#08306b',
      numColors: 14,
      usePalette: true
    });
    //provide('isDrawing', isDrawing);
    provide('result', result);
    provide('idwAction', idwAction);
    provide('isBathmetry', is_bathmetry);
    provide('isOffcanvas', isOffcanvas);
    provide('offcanvasWidth', offcanvasWidth);
    provide('newChart', newChart);
    provide('filterChart', filterChart);
    provide('colorChart', colorChart);
    provide('selectedPoint', selectedPoint);
    provide('isLive', is_live);
    provide('selected_area', selected_area);
    provide('redPaletteConfig', redPaletteConfig);
    provide('greenPaletteConfig', greenPaletteConfig);
    provide('bluePaletteConfig', bluePaletteConfig);
    provide('isPalette', isPalette);
    provide('activePalette', activePalette);
    provide('formulae_result1', formulae_result1);
    provide('formulae_result2', formulae_result2);
    provide('formulae_result3', formulae_result3);
    // Калибровочные кривые на канал; MapContainer/FlouView подмешивают их в IDW-запрос.
    provide('selectedCalRId', selectedCalRId);
    provide('selectedCalGId', selectedCalGId);
    provide('selectedCalBId', selectedCalBId);
    provide('stat', stat);
    provide('isTrackOn', isTrackOn);
    provide('isAnimationOn', isAnimationOn);
    provide('profilingPointsOn', profilingPointsOn);
    provide('profilingFitOn', profilingFitOn);
    provide('profilingPointSize', profilingPointSize);

    // Reactive bridge between AddWaterMaskModal (in NavBar) and MapContainer
    // (rendered via router-view). NavBar flips `active = true` to ask the map
    // to enter polygon-draw mode; the map writes the resulting [lon,lat] ring
    // into `result` and flips `active` back to false.
    const polygonDrawRequest = reactive({ active: false, result: null });
    provide('polygonDrawRequest', polygonDrawRequest);

    // Reactive bridge for previewing an area's water_geojson + poly on the
    // map. NavBar fetches the payload and flips `active = true`; MapContainer
    // renders the layers, and on "Clear preview" flips `active` back to
    // false. NavBar watches that to reopen the edit modal afterwards.
    const areaPreviewRequest = reactive({ active: false, payload: null });
    provide('areaPreviewRequest', areaPreviewRequest);

    //const startDate = new Date("2020-07-09T22:00:00");
    //const endDate = new Date("2020-07-10T07:00:00");
    //date.value = [startDate, endDate];
    const setDate = (value, isFromOpenDialog = true) => {
      date.value = value;
      if (isFromOpenDialog) {
        if(!store.getters.stateIsCustomData)
          store.commit('setIsCustomData', true);
      }
    };
    const colors = [];
    const numbers = [];
    const signature = "";
    // Статические палитры для fallback
    const red_colors = [
      [179,62,35], [181,61,35], [187,66,39], [215,79,45], [235,102,51],
      [237,106,52], [239,114,52], [238,119,52], [240,126,53], [237,137,52],
      [240,151,75], [243,170,102], [251,207,172], [253,235,223]
    ];
    const red_numbers = [3.4, 2.84, 2.28, 1.72, 1.16, 0.6, 0.54, 0.49, 0.43, 0.32, 0.26, 0.21, 0.15, 0.1, 0.04];
    const red_signature = "CDOM a440 (1/mu)";
    
    const green_colors = [
      [42,86,32], [42,91,36], [46,96,36], [49,100,41], [50,105,43],
      [56,111,51], [61,123,60], [67,134,71], [75,153,91], [94,185,124],
      [132,208,162], [176,225,196], [228,249,237], [233,250,240],
      [236,249,243], [241,252,248], [246,252,252], [244,254,253], [247,253,253]
    ];
    const green_numbers = ["",1, 0.925, 0.85, 0.775, 0.7, 0.625, 0.55, 0.475, 0.4, 0.325, 0.25, 0.175, 0.1, 0.0925, 0.085, 0.0775, 0.07, 0.0625, 0.055];
    const green_signature = "Chl-a (mg/L)";
    
    const blue_colors = [
      [8,48,107], [7,52,119], [8,56,132], [6,60,146], [7,66,158],
      [10,69,161], [16,72,165], [20,80,168], [29,86,175], [60,112,195],
      [66,117,198], [74,123,200], [81,129,205], [93,137,208], [104,146,212],
      [149,178,222], [215,225,248], [246,250,254], [247,250,255]
    ];
    const blue_numbers = ["",10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1];
    const blue_signature = "TSS (mg/L)";
    return {
      isInfo,
      date,
      datePickerKey,
      setDate,
      stat,
      tableDate,
      result,
      isLoadingTimerange,
      idwAction,
      red_colors,
      red_numbers,
      isPalette,
      colors,
      numbers,
      red_signature,
      signature,
      isDrawing,
      isStarted,
      switchPalette,
      green_colors,
      green_numbers,
      green_signature,
      blue_colors,
      blue_numbers,
      blue_signature,
      isOffcanvas,
      offcanvasWidth,
      isOffcanvasLocked,
      offcanvasStateBeforeLock,
      toggleOptionPanel,
      is_local,
      is_bathmetry,
      selected_chart,
      chartColor,
      newChart,
      filterChart,
      colorChart,
      selectedPoint,
      chartsTabSig,
      chartsTabNorm,
      chartsTabCalId,
      charts,
      chart_labels,
      idwText,
      profilingLiveActive,
      idw_progress,
      is_live,
      areas,
      selected_area,
      selected_area_name,
      mins1,
      mins2,
      mins1_r,
      mins2_r,
      mins1_g,
      mins2_g,
      mins1_b,
      mins2_b,
      cals,
      paramMinByLabel,
      selected_min1,
      selected_min2,
      selected_cal,
      paletteInfo,
      redPaletteConfig,
      greenPaletteConfig,
      bluePaletteConfig,
      activePalette,
      selected_min1_r,
      selected_min2_r,
      selected_cal_r,
      selected_min1_g,
      selected_min2_g,
      selected_cal_g,
      selected_min1_b,
      selected_min2_b,
      selected_cal_b,
      formulae_result1,
      formulae_result2,
      formulae_result3,
      selectedCalRId,
      selectedSigR,
      selectedNormR,
      signals,
      norms,
      lasers,
      selectedCalGId,
      selectedSigG,
      selectedNormG,
      selectedCalBId,
      selectedSigB,
      selectedNormB,
      compatibleNormsR,
      compatibleNormsG,
      compatibleNormsB,
      loadingNormsR,
      loadingNormsG,
      loadingNormsB,
      compatibleNormsCharts,
      loadingNormsCharts,
      compatibleNormMinsR,
      compatibleNormMinsG,
      compatibleNormMinsB,
      compatibleNormMinsCharts,
      isTrackOn,
      isAnimationOn,
      profilingAvgTime,
      profilingPointsOn,
      profilingFitOn,
      profilingPointSize,
      paletteWrapper,
      polygonDrawRequest,
      areaPreviewRequest
    }
  },
  components: {
    VueDatePicker,
    ColorPalette,
    CalibrationFormModal,
    CalibrationTableModal,
    EditParameterNamesModal,
    AddWaterMaskModal,
    PrintReportModal
  },
  beforeUnmount() {
    // Гарантируем остановку live-таймера при уничтожении компонента.
    this.stopLiveRefresh();
  },
  mounted() {
    if(this.is_custom_data) {
      if (this.$store.getters.stateDateStart && this.$store.getters.stateDateEnd)
        this.setDate([new Date(this.$store.getters.stateDateStart),
                           new Date(this.$store.getters.stateDateEnd)], false);
    }
    else {
      if (this.$store.getters.stateCompleteDateStart && this.$store.getters.stateCompleteDateEnd)
        this.setDate([new Date(this.$store.getters.stateCompleteDateStart),
                           new Date(this.$store.getters.stateCompleteDateEnd)], false);
    }
    this.setLoadingDataState(false);
    this.heatmap_vm = false;
    this.is_fit_vm = false;
    this.switchPalette = !this.switchPalette;
    this.is_bathmetry = false;
    if (process.env.VUE_APP_MAP_SOURCE === 'local')
      this.is_local = true;
    else
      this.is_local = false;
    this.getAreas();
    this.updateActivePalette();
    this.$store.dispatch('loadCalibrations');
    this.editableTitles = this.$store.getters.paramTitles;
  },
  computed: {
    ...mapGetters(['paramTitles']),
    ...mapGetters('chartsParams', ['chartsEnabledParameters']),
    editableSelectedArea() {
      // Only UI-uploaded masks (water_geojson stored inline) are editable;
      // legacy file-based areas have no payload to round-trip through the form.
      if (!this.selected_area_name) return null;
      const area = this.areas.find(a => a.name === this.selected_area_name);
      return area && area.has_water_geojson ? area : null;
    },
    calibrations() {
      return this.$store.getters.allCalibrations || [];
    },
    selectedRCal() {
      return this.selectedCalRId ? this.calibrations.find(c => c.id === this.selectedCalRId) : "No";
    },
    selectedGCal() {
      return this.selectedCalGId ? this.calibrations.find(c => c.id === this.selectedCalGId) : "No";
    },
    selectedBCal() {
      return this.selectedCalBId ? this.calibrations.find(c => c.id === this.selectedCalBId) : "No";
    },
    activeChartsChannel() {
      if (this.r_vm) return 'r';
      if (this.g_vm) return 'g';
      if (this.b_vm) return 'b';
      return null;
    },
    activeChartsSignal: {
      get() {
        const ch = this.activeChartsChannel;
        if (ch === 'r') return this.selectedSigR;
        if (ch === 'g') return this.selectedSigG;
        if (ch === 'b') return this.selectedSigB;
        return this.chartsTabSig || (this.signals && this.signals[0]) || '';
      },
      set(v) {
        const ch = this.activeChartsChannel;
        if (ch === 'r') this.selectedSigR = v;
        else if (ch === 'g') this.selectedSigG = v;
        else if (ch === 'b') this.selectedSigB = v;
        else this.chartsTabSig = v;
      }
    },
    activeChartsNormalization: {
      get() {
        const ch = this.activeChartsChannel;
        if (ch === 'r') return this.selectedNormR;
        if (ch === 'g') return this.selectedNormG;
        if (ch === 'b') return this.selectedNormB;
        return this.chartsTabNorm || (this.norms && this.norms[0]) || '';
      },
      set(v) {
        const ch = this.activeChartsChannel;
        if (ch === 'r') this.selectedNormR = v;
        else if (ch === 'g') this.selectedNormG = v;
        else if (ch === 'b') this.selectedNormB = v;
        else this.chartsTabNorm = v;
      }
    },
    activeChartsCalibrationId: {
      get() {
        const ch = this.activeChartsChannel;
        if (ch === 'r') return this.selectedCalRId || '';
        if (ch === 'g') return this.selectedCalGId || '';
        if (ch === 'b') return this.selectedCalBId || '';
        return this.chartsTabCalId || '';
      },
      set(v) {
        const ch = this.activeChartsChannel;
        if (ch === 'r') this.selectedCalRId = v;
        else if (ch === 'g') this.selectedCalGId = v;
        else if (ch === 'b') this.selectedCalBId = v;
        else this.chartsTabCalId = v;
      }
    },
    activeChartsCal() {
      const id = this.activeChartsCalibrationId;
      return id ? (this.calibrations.find(c => c.id === id) || 'No') : 'No';
    },
    chartsCompatibleNorms() {
      const ch = this.activeChartsChannel;
      if (ch === 'r') return this.compatibleNormsR;
      if (ch === 'g') return this.compatibleNormsG;
      if (ch === 'b') return this.compatibleNormsB;
      return this.compatibleNormsCharts;
    },
    chartsNormalizationDisabled() {
      const ch = this.activeChartsChannel;
      if (ch === 'r') return !this.selectedSigR || this.loadingNormsR;
      if (ch === 'g') return !this.selectedSigG || this.loadingNormsG;
      if (ch === 'b') return !this.selectedSigB || this.loadingNormsB;
      return !this.chartsTabSig || this.loadingNormsCharts;
    },
    isLoggedIn: function() {
      return this.$store.getters.isAuthenticated;
    },
    isAdmin: function() {
      return this.$store.getters.isAdmin;
    },
    lidars() {
      return this.$store.getters.stateLidars;
    },
    selectedLidarId() {
      return this.$store.getters.selectedLidarId;
    },
    hasLidarChoice() {
      return this.$store.getters.hasLidarChoice;
    },
    isLocalMode: function() {
      return process.env.VUE_APP_MODE === 'local';
    },
    currentRouteName() {
        return this.$route.name;
    },
    heatmap_vm: {
      get () { return this.$store.getters.stateIsHeatmap},
      set (value) { this.setHeatmapState(value)}
    },
    is_fit_vm: {
      get () { return this.$store.getters.stateIsFit},
      set (value) { this.setIsFitState(value)}
    },
    idw_vm: {
      get () { return this.$store.getters.stateIsIDW},
      set (value) { this.setIDWState(value)}
    },
    isLoadingData: function() { return this.$store.getters.stateIsLoadingData},
    r_vm: {
      get () { return this.$store.getters.stateIsR},
      set (value) { 
        this.setRState(value);
        this.switchPalette = !this.switchPalette;
        this.updateActivePalette();
      }
    },
    g_vm: {
      get () { return this.$store.getters.stateIsG},
      set (value) {
        this.setGState(value);
        this.switchPalette = !this.switchPalette;
        this.updateActivePalette();
      }
    },
    b_vm: {
      get () { return this.$store.getters.stateIsB},
      set (value) {
        this.setBState(value);
        this.switchPalette = !this.switchPalette;
        this.updateActivePalette();
      }
    },
    log_vm: {
      get () { return this.$store.getters.stateIsLog},
      set (value) { this.setLogState(value)}
    },
    is_r_range: {
      get () { return this.$store.getters.stateIsRRange},
      set (value) { this.setIsRRangeState(value)}      
    },
    r_min: {
      get () { return this.$store.getters.stateRMin},
      set (value) { this.setRMinState(value)}      
    },
    r_max: {
      get () { return this.$store.getters.stateRMax},
      set (value) { this.setRMaxState(value)}      
    },
    is_r_filter: {
      get () { return this.$store.getters.stateIsRFilter},
      set (value) { this.setIsRFilterState(value)}      
    },
    r_filter_min: {
      get () { return this.$store.getters.stateRFilterMin},
      set (value) { this.setRFilterMinState(value)}      
    },
    r_filter_max: {
      get () { return this.$store.getters.stateRFilterMax},
      set (value) { this.setRFilterMaxState(value)}      
    },
    is_g_range: {
      get () { return this.$store.getters.stateIsGRange},
      set (value) { this.setIsGRangeState(value)}      
    },
    g_min: {
      get () { return this.$store.getters.stateGMin},
      set (value) { this.setGMinState(value)}      
    },
    g_max: {
      get () { return this.$store.getters.stateGMax},
      set (value) { this.setGMaxState(value)}      
    },
    is_g_filter: {
      get () { return this.$store.getters.stateIsGFilter},
      set (value) { this.setIsGFilterState(value)}      
    },
    g_filter_min: {
      get () { return this.$store.getters.stateGFilterMin},
      set (value) { this.setGFilterMinState(value)}      
    },
    g_filter_max: {
      get () { return this.$store.getters.stateGFilterMax},
      set (value) { this.setGFilterMaxState(value)}      
    },
    is_b_range: {
      get () { return this.$store.getters.stateIsBRange},
      set (value) { this.setIsBRangeState(value)}      
    },
    b_min: {
      get () { return this.$store.getters.stateBMin},
      set (value) { this.setBMinState(value)}      
    },
    b_max: {
      get () { return this.$store.getters.stateBMax},
      set (value) { this.setBMaxState(value)}      
    },
    is_b_filter: {
      get () { return this.$store.getters.stateIsBFilter},
      set (value) { this.setIsBFilterState(value)}      
    },
    b_filter_min: {
      get () { return this.$store.getters.stateBFilterMin},
      set (value) { this.setBFilterMinState(value)}      
    },
    b_filter_max: {
      get () { return this.$store.getters.stateBFilterMax},
      set (value) { this.setBFilterMaxState(value)}      
    },
    maxd: {
      get () { return this.$store.getters.stateMaxD},
      set (value) { this.setMaxDState(value)}      
    },
    scale: {
      get () { return this.$store.getters.stateScale},
      set (value) { this.setScaleState(value)}      
    },
    idw_opacity: {
      get () { return this.$store.getters.stateIdwOpacity},
      set (value) { return this.setIdwOpacityState(value)}
    },
    is_custom_data: {
      get () { return this.$store.getters.stateIsCustomData},
      set (value) { return this.setIsCustomDataState(value)}
    },
    is_ssc: {
       get () { return this.$store.getters.stateIsServerSideCalc},
       set (value) {
        this.is_water_mask = value;
        return this.setIsServerSideCalcState(value);
      }
    },
    is_water_mask: {
       get () { return this.$store.getters.stateIsWaterMask },
       set (value) { return this.setIsWaterMaskState(value)}
    },
    hmPointSize: {
      get () { return this.$store.getters.stateHmPointSize},
      set (value) { this.setHmPointSizeState(value)}      
    },
    avgTime: {
      get () { return this.$store.getters.stateAvgTime},
      set (value) { this.setAvgTimeState(value)}      
    },
    intervalTime: {
      get () { return this.$store.getters.stateIntervalTime},
      set (value) { this.setIntervalTimeState(value)}
    },
    // Поле "Laser, nm" — одно значение на все вкладки (Map/Profiling/Fluo).
    // Уходит в запросы как параметр `laser`; бэкенд превращает его в фильтр
    // по ell_18_2_pos. Пустая строка = фильтра нет.
    laser: {
      get () { return this.$store.getters.stateLaser},
      set (value) { this.setLaserState(value)}
    },
    profilingLogScale: {
      get () { return this.$store.getters['profilingParams/profilingLogScale']},
      set (value) { this.$store.commit('profilingParams/SET_PROFILING_LOG_SCALE', value)}
    },
    // Текст кнопки на вкладке Profiling: Search (обычный режим) → Apply (live, не
    // запущен) → Cancel (live-опрос идёт).
    profilingBtnText () {
      if (!this.is_live) return "Search";
      return this.profilingLiveActive ? "Cancel" : "Apply";
    }
  },
  watch: {
    'polygonDrawRequest.result'(coords) {
      if (coords && coords.length >= 3 && this.$refs.addWaterMaskModal) {
        this.$refs.addWaterMaskModal.setPolygon(coords);
      }
    },
    'areaPreviewRequest.active'(active, wasActive) {
      // Flipping false after being true means the user clicked "Clear
      // preview" on the map — bring the edit modal back so they can keep
      // managing areas.
      if (wasActive && !active && this.$refs.addWaterMaskModal) {
        this.$refs.addWaterMaskModal.reopen();
      }
    },
    '$route.name': {
      immediate: true,
      handler(newName, oldName) {
        // Full-width pages (profile, admin) collapse the params panel.
        const isFull = (n) => n === 'profile' || n === 'admin';
        if (isFull(newName) && !isFull(oldName)) {
          this.offcanvasStateBeforeLock = this.isOffcanvas;
          this.isOffcanvas = false;
          this.isOffcanvasLocked = true;
          this.$nextTick(() => window.dispatchEvent(new Event('resize')));
        } else if (isFull(oldName) && !isFull(newName)) {
          this.isOffcanvasLocked = false;
          this.isOffcanvas = this.offcanvasStateBeforeLock;
          this.$nextTick(() => window.dispatchEvent(new Event('resize')));
        }
      },
    },
    is_live(newV, oldV) {
      if (oldV == newV) return;
      if (newV) {
        // Live включён: левая граница пикера = текущее время, правая ПУСТАЯ
        // (открытый интервал — конец всегда «сейчас»). Раньше здесь через
        // set_custom() правая граница заполнялась now, из-за чего пикер не
        // выглядел живым/открытым.
        this.snapLiveStartToNow();
      } else {
        // Live выключен — прекращаем все автообновления и сбрасываем состояние
        // кнопки профайлинга, возвращаемся к обычному выбору диапазона.
        this.stopLiveRefresh();
        this.profilingLiveActive = false;
        this.is_custom_data = false;
        this.set_custom();
      }
    }
    // isDrawing(newV, oldV) {
    //   if(oldV != newV) {
    //     this.idwText = newV ? "Stop" : "Apply";
    //   }
    // }
  },
  methods: {
    ...mapActions(['setHeatmapState', 'setIDWState', 'setRState',
      'setLoadingDataState', 'setGState', 'setBState', 'setLogState', 'setDateStartState', 'setDateEndState',
      'setRMinState', 'setRMaxState', 'setIsRRangeState', 'setMaxDState', 'setGMinState', 'setGMaxState', 'setIsGRangeState',
      'setBMinState', 'setBMaxState', 'setIsBRangeState', 'setIdwOpacityState', 'setIsCustomDataState', 'setTimeRangeState', 'setIsFitState',
      'setRFilterMinState', 'setRFilterMaxState', 'setIsRFilterState','setGFilterMinState', 'setGFilterMaxState', 'setIsGFilterState',
      'setBFilterMinState', 'setBFilterMaxState', 'setIsBFilterState', 'setScaleState', 'setIsServerSideCalcState', 'setIsWaterMaskState',
      'setHmPointSizeState', 'setAvgTimeState', 'setIntervalTimeState', 'setLaserState', 'updateParamTitle']),
    // Параметр `laser` для запросов: пустое значение не отправляем, чтобы
    // бэкенд работал как раньше (без фильтра по ell_18_2_pos).
    withLaser(params) {
      if (this.laser) params.laser = this.laser;
      return params;
    },
    // Начало выбранного диапазона в ISO. Null-safe: если левая граница пикера
    // пуста — берём текущий момент (для открытых live-диапазонов).
    dateStartIso() {
      return (this.date && this.date[0] ? this.date[0] : new Date()).toISOString();
    },
    // Конец диапазона в ISO. В live правая граница пикера пустая (открытый
    // интервал) — тогда конец = «сейчас», сервер стримит до текущего момента.
    dateEndIso() {
      return (this.date && this.date[1] ? this.date[1] : new Date()).toISOString();
    },
    // Live: по нажатию Apply / Add chart отсчёт начинается с ТЕКУЩЕГО момента.
    // Пикер показывает [now, пусто], а dateStart/End и isCustomData
    // выставляются так, чтобы интерполяция и графики стартовали с этой точки
    // (см. MapContainer.addIDWLayer: start = isCustomData ? dateStart : ...).
    snapLiveStartToNow() {
      const now = new Date();
      this.setDate([now, null], false);
      this.datePickerKey++;
      this.is_custom_data = true;
      this.setDateStartState(now.toISOString());
      this.setDateEndState(now.toISOString());
    },
    // Search (не live): фиксируем правую границу диапазона на момент нажатия.
    // Если пользователь задал ТОЛЬКО начало (правая пуста) — достраиваем конец
    // = «сейчас» одним зафиксированным значением, чтобы все под-запросы (данные,
    // графики, трек) использовали один и тот же end и пикер показывал диапазон.
    // Обе границы заданы → не трогаем (ручной произвольный диапазон). Обе пусты
    // → ничего не делаем (остаётся режим «весь датасет», is_custom_data=false).
    snapSearchEndToNow() {
      const hasStart = !!(this.date && this.date[0]);
      const hasEnd = !!(this.date && this.date[1]);
      if (hasStart && !hasEnd) {
        const start = new Date(this.date[0]);
        const end = new Date();
        this.setDate([start, end], false);
        this.datePickerKey++;
        this.is_custom_data = true;
        this.setDateStartState(start.toISOString());
        this.setDateEndState(end.toISOString());
      }
    },
    openEditModal() {
      this.$refs.editNamesModal.open();
    },
    updateTitles(newTitles) {
      newTitles.forEach((title, index) => {
        this.updateParamTitle({ index, title });
      });
    },
    saveTitles() {
      this.editableTitles.forEach((title, index) => {
        this.updateParamTitle({ index, title });
      });
      this.showEditModal = false;
    },
    beforeUnmount() {
      this.result = null;
      this.newChart = null;
      this.filterChart = null;
    },
    updateActivePalette() {
      if (this.$store.getters.stateIsR && !this.$store.getters.stateIsG && !this.$store.getters.stateIsB) {
        this.setActivePalette('red');
        this.isPalette = true;
      } else if (!this.$store.getters.stateIsR && this.$store.getters.stateIsG && !this.$store.getters.stateIsB) {
        this.setActivePalette('green');
        this.isPalette = true;
      } else if (!this.$store.getters.stateIsR && !this.$store.getters.stateIsG && this.$store.getters.stateIsB) {
        this.setActivePalette('blue');
        this.isPalette = true;
      } else {
        this.activePalette = null;
        this.isPalette = false;
      }
    },

    setActivePalette(channel) {
      // const titles = this.paramTitles; // получили из Vuex
      const isLog = this.log_vm; // или другой способ узнать лог. масштаб

      let colors = [], numbers = [], signature = '';

      if (this.paletteInfo && this.paletteInfo[channel]) {
        const info = this.paletteInfo[channel];
        colors = info.colors || [];
        numbers = info.numbers || [];
        // Формируем подпись
        const logSuffix = isLog ? ' (log)' : '';
        // const titleIndex = channel === 'red' ? 0 : channel === 'green' ? 1 : 2;
        let dimension = '';
        let chart_label = '';
        const titleFor = (idx, sig) => (sig === 'No' || !sig)
          ? (this.paramTitles[idx] || `Measuring parameter ${idx + 1}`)
          : sig.split('(')[0].trim();
        if(this.r_vm && !this.g_vm && !this.b_vm)
          chart_label = titleFor(0, this.selectedSigR);
          dimension += this.selectedRCal.rangeUnit ? this.selectedRCal.rangeUnit : '';
        if(!this.r_vm && this.g_vm && !this.b_vm)
          chart_label = titleFor(1, this.selectedSigG);
          dimension += this.selectedGCal.rangeUnit ? this.selectedGCal.rangeUnit : '';
        if(!this.r_vm && !this.g_vm && this.b_vm)
          chart_label = titleFor(2, this.selectedSigB);
          dimension += this.selectedBCal.rangeUnit ? this.selectedBCal.rangeUnit : '';
        if (dimension)
          dimension = ', ' + dimension;
        else
          dimension = ', a.u.'
        signature = `${chart_label}${dimension}${logSuffix}`;
      } else {
        // Если нет данных палитры, используем заглушку или пусто
        colors = [];
        numbers = [];
        signature = '';
      }

      this.activePalette = {
        colors,
        numbers,
        signature
      };
      this.isPalette = true;
    },

    processDrawing (status, progress) {
      if (progress || progress == 0)
        this.idw_progress = progress;
      if (status === "taskstarted") {
        this.isStarted = true;
        this.idwText = "Cancel";
        // Live-интерполяция пошла — на вкладке Map начинаем инкрементально
        // продлевать трек тем же интервалом, что и опрос интерполяции.
        if (this.is_live && this.currentRouteName === 'map') {
          this.startLiveRefresh({ track: true, profiling: false });
        }
        return;
      }
      else if (status === "taskcancelled")
        this.idw_progress = 0;
      this.isDrawing = false;
      this.isStarted = false;
      this.idwText = "Apply";
      this.idwAction = 'finish';
      // Интерполяция остановлена/завершена — гасим live-опрос трека вкладки Map
      // (профайлинг-таб управляет своим циклом отдельно).
      if (!this.profilingLiveActive) this.stopLiveRefresh();
    },
    // --- Live-обновление трека и профайлинга ---------------------------------
    startLiveRefresh(opts = { track: false, profiling: false }) {
      this._liveRefreshOpts = { ...opts };
      this.stopLiveRefresh(true); // сбрасываем прежний таймер, сохраняя opts
      const seconds = Number(this.$store.getters.stateIntervalTime) || 5;
      const ms = Math.max(1, seconds) * 1000;
      this._liveRefreshTimer = setInterval(() => this.liveRefreshTick(), ms);
    },
    stopLiveRefresh(keepOpts = false) {
      if (this._liveRefreshTimer) {
        clearInterval(this._liveRefreshTimer);
        this._liveRefreshTimer = null;
      }
      if (!keepOpts) this._liveRefreshOpts = { track: false, profiling: false };
    },
    async liveRefreshTick() {
      // Пропускаем тик, если предыдущий запрос ещё в полёте, чтобы не копить
      // очередь при медленном бэкенде.
      if (this._liveRefreshBusy) return;
      const opts = this._liveRefreshOpts || {};
      if (!opts.track && !opts.profiling) return;
      this._liveRefreshBusy = true;
      try {
        const now = new Date().toISOString();
        const jobs = [];
        if (opts.track && this.isTrackOn) {
          jobs.push(this.$store.dispatch('appendTrackPoints', { end_time: now, raw: true }));
        }
        if (opts.profiling) {
          jobs.push(this.$store.dispatch('profilingParams/refreshProfilingPointsLive', { endTime: now }));
        }
        await Promise.all(jobs);
      } catch (error) {
        console.error('Live refresh tick failed:', error);
      } finally {
        this._liveRefreshBusy = false;
      }
    },
    paletteChange (paletteInfo) {
      this.paletteInfo = paletteInfo;
      this.updateActivePalette();
    },
    selectLidar(id) {
      if (id === this.selectedLidarId) return;
      this.$store.dispatch('selectLidar', id);
      // Track, charts, profiling and interpolation caches all belong to the
      // previous lidar; a reload is the simplest way to drop every one of them.
      window.location.reload();
    },
    async logout () {
      await this.$store.dispatch('logOut');
      this.$router.push('/login');
    },
    async getAreas() {
      let {data} = await axios.get(`areas`);
      this.areas = data;
      // const params = {
      //   area_id: "",
      // }
      // await axios.post('/set_area', null,  { params });

    },
    openAddWaterMask() {
      this.$refs.addWaterMaskModal.open();
    },
    openPrintReport() {
      this.$refs.printReportModal.open();
    },
    async openEditWaterMask() {
      // Always available. Refresh the list first so the dropdown reflects
      // any out-of-band changes; then open the modal in edit mode with the
      // currently-selected area pre-picked (or the first one).
      await this.getAreas();
      if (!this.areas.length) {
        // eslint-disable-next-line no-alert
        window.alert('No areas exist yet. Use "Add new" to create one.');
        return;
      }
      const preferred =
        this.areas.find((a) => a.name === this.selected_area_name)
        || this.areas[0];
      this.$refs.addWaterMaskModal.open(preferred, this.areas);
    },
    async handleWaterMaskDeleted(payload) {
      // Refresh local areas list; if the deleted area was the user's selection,
      // clear it so downstream consumers don't reference a gone id.
      const wasSelectedId = this.selected_area && this.selected_area.id === payload.id;
      await this.getAreas();
      if (wasSelectedId) {
        this.selected_area = null;
        this.selected_area_name = 'Choose site...';
      }
    },
    async handlePreviewArea({ id }) {
      try {
        const { data } = await axios.get(`/areas/${id}/preview`);
        this.areaPreviewRequest.payload = data;
        this.areaPreviewRequest.active = true;
        // When the user clears the preview from the map, MapContainer flips
        // active back to false; we watch that and re-open the modal.
      } catch (err) {
        console.error('Preview failed', err);
        this.$refs.addWaterMaskModal.reopen();
      }
    },
    handleRequestPolygon() {
      // Modal closed itself; ask MapContainer to enter draw mode.
      this.polygonDrawRequest.result = null;
      this.polygonDrawRequest.active = true;
    },
    async handleWaterMaskSaved(area) {
      await this.getAreas();
      if (area && area.name) {
        this.selected_area_name = area.name;
        await this.handleAreaChange();
      }
    },
    async handleAreaChange () {
      this.areas.forEach(area => {
        if(area.name === this.selected_area_name) {
          this.selected_area = area;
        }
      })
      const params = {
        area_id: this.selected_area.id,
      }
      try {
        // this.is_live = false;
        // this.isLoadingTimerange = true;
        // await this.setLoadingDataState(true);
        // await axios.post('/set_area', null,  { params });
        // let {data} = await axios.get(`time_range`);

        // await this.setTimeRangeState(data);
        // if (this.$store.getters.stateCompleteDateStart && this.$store.getters.stateCompleteDateEnd)
        //   this.setDate([new Date(this.$store.getters.stateCompleteDateStart),
        //                  new Date(this.$store.getters.stateCompleteDateEnd)]);
        // this.is_custom_data = false;
        // await this.setLoadingDataState(false);
        // this.isLoadingTimerange = false;
        await axios.post('/set_area', null,  { params });
      } catch(error) {
        console.error(error);
      }

    },
    async handleIsWaterMask () {
      try {
        if (!this.is_water_mask) {
          const params = {
            area_id: "",
          }
          await axios.post('/set_area', null,  { params });
        }
        else {
          await this.handleAreaChange();
        }

      }
      catch(error) {
        console.error(error);
      }
    },
    async add_chart() {
      try {
          await this.get_params();
          // Live: новый график начинает строиться с момента нажатия Add chart.
          // Снап делаем ПОСЛЕ get_params (ему нужен исторический диапазон для
          // списка сигналов), но ДО запроса данных графика — тогда пикер
          // показывает [now, пусто], а точки идут от текущего момента.
          // Non-live end=now уже зафиксирован внутри get_params() выше; в live
          // снапим начало на текущий момент (правая граница остаётся пустой).
          if (this.is_live) this.snapLiveStartToNow();
          await this.setLoadingDataState(true);
          this.heatmap_vm = false;
          if(this.is_custom_data) {
            await this.setDateStartState(this.dateStartIso());
            await this.setDateEndState(this.dateEndIso());
          }
          this.tableDate = this.date;
          const cal = this.activeChartsCal;
          const sig = this.activeChartsSignal;
          const norm = this.activeChartsNormalization;
          // Полином калибровки применяется на бэкенде по calibration_id,
          // поэтому в самой формуле всегда множитель 1.
          // Аналогично draw(): резолвим Norm в MIN из ограниченного по каналу списка.
          const chartNormMins = (this.compatibleNormMinsCharts && this.compatibleNormMinsCharts.length) ? this.compatibleNormMinsCharts : this.mins2;
          const chart_type = '1×' + await this.findMatchingMin(sig, this.mins1) + '÷' + await this.findMatchingMin(norm, chartNormMins);
          let dimension = ''
          dimension += (cal && cal.rangeUnit) ? cal.rangeUnit : '';
          if (dimension)
            dimension = ', ' + dimension;
          else
            dimension = ', a.u.'
          const chart_label = (sig === 'No') ? '' : ((sig ? sig.split('(')[0].trim() : '') + dimension);
          const chart = {
            labels: [],
            data: [],
            type: chart_label,
            // Формула (и калибровка) нужны для live-опроса /measurements/latest:
            // без них ChartsView шлёт param = подпись графика вместо формулы,
            // бэкенд возвращает пусто и серия затирается (остаётся только ось X).
            formula: chart_type,
            calibrationId: this.activeChartsCalibrationId || '',
            color: this.chartColor,
            id: Date.now() + Math.random(),
            is_filter: false,
            filter_min: null,
            filter_max: null,
            logScale: !!this.log_vm,
            // Собственный left-край live-опроса этого графика. В live snap уже
            // сдвинул dateStart на «сейчас», поэтому новый график стартует с
            // текущего момента, не затрагивая окна ранее созданных графиков.
            startTime: this.dateStartIso(),
          };

          const params = {
            start_time: this.dateStartIso(),
            end_time: this.dateEndIso(),
            avg_time: this.avgTime,
            param: chart_type,
          }
          // calibration_id применяет полином y=a*x^3+b*x^2+c*x+d к результату формулы.
          if (this.activeChartsCalibrationId) params.calibration_id = this.activeChartsCalibrationId;
          const response = await axios.get('measurements/param', { params: this.withLaser(params) });
          chart.data = response.data.values;
          chart.labels = response.data.times;
          this.newChart = chart;
          this.charts.add({
            type: chart.type, 
            color: chart.color, 
            id: chart.id, 
            is_filter: chart.is_filter,
            filter_min: chart.filter_min, 
            filter_max: chart.filter_max
          });
          await this.setLoadingDataState(false);
        
      } catch(error) {
        console.error(error);
        await this.setLoadingDataState(false);
      }
    },
    remove_chart(chart) {
      this.charts.delete(chart);
      chart.data = null;
      this.newChart = chart;
    },
    apply_chart_filter(chart) {
      const updatedCharts = new Set(this.charts);
      updatedCharts.forEach(c => {
        if (c.id === chart.id) {
          c.is_filter = chart.is_filter;
          c.filter_min = chart.filter_min;
          c.filter_max = chart.filter_max;
        }
      });
      this.charts = updatedCharts;
      Object.assign(this.filterChart, chart);
    },

    async load () {
      try {
        await this.setLoadingDataState(true);
        this.heatmap_vm = false;
        // Пересоздаём статистику с нуля: иначе отключённые в этом запуске
        // каналы (r/g/b) остаются от прошлого расчёта и лишней строкой
        // попадают в Print Report.
        this.stat = {};
        if(this.is_custom_data) {
          await this.setDateStartState(this.dateStartIso());
          await this.setDateEndState(this.dateEndIso());
        }
        this.tableDate = this.date;
        // const params = {
        //   start_time: this.dateStartIso(),
        //   end_time: this.dateEndIso(),
        //   avg_time: this.avgTime
        // }
        // const response = await axios.get('measurements/stats', { params });
        // this.stat = JSON.parse(response.data.stat);
        // this.result.data = JSON.parse(response.data.data);
        if (this.r_vm) {
          const params = {
              start_time: this.dateStartIso(),
              end_time: this.dateEndIso(),
              avg_time: this.avgTime,
              param: this.formulae_result1,
              is_stat: true,
              is_poly: true,
            }
          if (this.is_r_filter && this.r_filter_min)
            params['min_value'] = this.r_filter_min
          if (this.is_r_filter && this.r_filter_max)
            params['max_value'] = this.r_filter_max
          // Применяем калибровочный полином по выбранной кривой R-канала.
          if (this.selectedCalRId) params['calibration_id'] = this.selectedCalRId;
          const response = await axios.get('measurements/param', { params: this.withLaser(params) });
          this.stat['r'] = response.data;
          const start_date = new Date(response.data.times[0]);
          this.stat['start_date'] = start_date.toUTCString();
          const end_date = new Date(response.data.times.at(-1));
          this.stat['end_date'] = end_date.toUTCString();
          this.stat['r']['count'] = response.data.values.length;
        }
        if (this.g_vm) {
          const params = {
              start_time: this.dateStartIso(),
              end_time: this.dateEndIso(),
              avg_time: this.avgTime,
              param: this.formulae_result2,
              is_stat: true,
              is_poly: true,
            }
          if (this.is_g_filter && this.g_filter_min)
            params['min_value'] = this.g_filter_min
          if (this.is_g_filter && this.g_filter_max)
            params['max_value'] = this.g_filter_max
          if (this.selectedCalGId) params['calibration_id'] = this.selectedCalGId;
          const response = await axios.get('measurements/param', { params: this.withLaser(params) });
          this.stat['g'] = response.data;
          const start_date = new Date(response.data.times[0]);
          this.stat['start_date'] = start_date.toUTCString();
          const end_date = new Date(response.data.times.at(-1));
          this.stat['end_date'] = end_date.toUTCString();
          this.stat['g']['count'] = response.data.values.length;
        }
        if(this.b_vm) {
          const params = {
              start_time: this.dateStartIso(),
              end_time: this.dateEndIso(),
              avg_time: this.avgTime,
              param: this.formulae_result3,
              is_stat: true,
              is_poly: true,
            }
          if (this.is_b_filter && this.b_filter_min)
            params['min_value'] = this.b_filter_min
          if (this.is_b_filter && this.b_filter_max)
            params['max_value'] = this.b_filter_max
          if (this.selectedCalBId) params['calibration_id'] = this.selectedCalBId;
          const response = await axios.get('measurements/param', { params: this.withLaser(params) });
          this.stat['b'] = response.data;
          const start_date = new Date(response.data.times[0]);
          this.stat['start_date'] = start_date.toUTCString();
          const end_date = new Date(response.data.times.at(-1));
          this.stat['end_date'] = end_date.toUTCString();
          this.stat['b']['count'] = response.data.values.length; 
        }
        await this.setLoadingDataState(false);

      } catch(error) {
        console.error(error);
        await this.setLoadingDataState(false);
      }
    },
    async load_fluo () {
      try {
        await this.setLoadingDataState(true);
        this.heatmap_vm = false;
        // См. load(): пересоздаём статистику, чтобы не тащить старые каналы.
        this.stat = {};
        if(this.is_custom_data) {
          await this.setDateStartState(this.dateStartIso());
          await this.setDateEndState(this.dateEndIso());
        }
        this.tableDate = this.date;
        // const params = {
        //   start_time: this.dateStartIso(),
        //   end_time: this.dateEndIso(),
        //   avg_time: this.avgTime
        // }
        // const response = await axios.get('measurements/stats', { params });
        // this.stat = JSON.parse(response.data.stat);
        // this.result.data = JSON.parse(response.data.data);
        if (this.r_vm) {
          const params = {
              start_time: this.dateStartIso(),
              end_time: this.dateEndIso(),
              avg_time: 100,
              param: this.formulae_result1,
              is_stat: true,
              is_poly: true,
            }
          if (this.is_r_filter && this.r_filter_min)
            params['min_value'] = this.r_filter_min
          if (this.is_r_filter && this.r_filter_max)
            params['max_value'] = this.r_filter_max
          // Применяем калибровочный полином по выбранной кривой R-канала.
          if (this.selectedCalRId) params['calibration_id'] = this.selectedCalRId;
          const response = await axios.get('measurements/param', { params: this.withLaser(params) });
          this.stat['r'] = response.data;
          const start_date = new Date(response.data.times[0]);
          this.stat['start_date'] = start_date.toUTCString();
          const end_date = new Date(response.data.times.at(-1));
          this.stat['end_date'] = end_date.toUTCString();
          this.stat['r']['count'] = response.data.values.length;
        }
        if (this.g_vm) {
          const params = {
              start_time: this.dateStartIso(),
              end_time: this.dateEndIso(),
              avg_time: 100,
              param: this.formulae_result2,
              is_stat: true,
              is_poly: true,
            }
          if (this.is_g_filter && this.g_filter_min)
            params['min_value'] = this.g_filter_min
          if (this.is_g_filter && this.g_filter_max)
            params['max_value'] = this.g_filter_max
          if (this.selectedCalGId) params['calibration_id'] = this.selectedCalGId;
          const response = await axios.get('measurements/param', { params: this.withLaser(params) });
          this.stat['g'] = response.data;
          const start_date = new Date(response.data.times[0]);
          this.stat['start_date'] = start_date.toUTCString();
          const end_date = new Date(response.data.times.at(-1));
          this.stat['end_date'] = end_date.toUTCString();
          this.stat['g']['count'] = response.data.values.length;
        }
        if(this.b_vm) {
          const params = {
              start_time: this.dateStartIso(),
              end_time: this.dateEndIso(),
              avg_time: 100,
              param: this.formulae_result3,
              is_stat: true,
              is_poly: true,
            }
          if (this.is_b_filter && this.b_filter_min)
            params['min_value'] = this.b_filter_min
          if (this.is_b_filter && this.b_filter_max)
            params['max_value'] = this.b_filter_max
          if (this.selectedCalBId) params['calibration_id'] = this.selectedCalBId;
          const response = await axios.get('measurements/param', { params: this.withLaser(params) });
          this.stat['b'] = response.data;
          const start_date = new Date(response.data.times[0]);
          this.stat['start_date'] = start_date.toUTCString();
          const end_date = new Date(response.data.times.at(-1));
          this.stat['end_date'] = end_date.toUTCString();
          this.stat['b']['count'] = response.data.values.length; 
        }
        await this.setLoadingDataState(false);
        this.heatmap_vm = true;

      } catch(error) {
        console.error(error);
        await this.setLoadingDataState(false);
      }
    },


    // Обработчик кнопки на вкладке Profiling. В обычном режиме — разовый Search.
    // В live-режиме — тумблер Apply/Cancel: первый клик грузит начальные данные
    // и запускает автообновление профайлинга + трека, второй — останавливает.
    async profilingButtonAction () {
      if (!this.is_live) {
        // Обычный поиск: фиксируем правую границу = «сейчас» на момент клика.
        this.snapSearchEndToNow();
        await this.get_profiling_params();
        return;
      }
      if (this.profilingLiveActive) {
        this.stopLiveRefresh();
        this.profilingLiveActive = false;
        return;
      }
      // Старт live-профайлинга: отсчёт с момента нажатия Apply (пикер [now,
      // пусто]), затем первичная загрузка и цикл опроса.
      this.snapLiveStartToNow();
      await this.get_profiling_params();
      this.profilingLiveActive = true;
      this.startLiveRefresh({ track: true, profiling: true });
    },
    async get_profiling_params () {
      // Profiling Search: fetches mode='Profiling' points (averaged into time
      // buckets) and a track for the same time range; track + animation reuse
      // the global isTrackOn / isAnimationOn flags injected into MapContainer.
      try {
        // Правая граница может быть пустой (открытый live-диапазон) — тогда
        // конец берётся как «сейчас» через dateEndIso(); требуем только начало.
        if (!this.date || !this.date[0]) return;
        await this.setLoadingDataState(true);
        if (this.is_custom_data) {
          await this.setDateStartState(this.dateStartIso());
          await this.setDateEndState(this.dateEndIso());
        }
        this.tableDate = this.date;

        const startIso = this.dateStartIso();
        const endIso = this.dateEndIso();

        await this.$store.dispatch('profilingParams/searchProfilingPoints', {
          startTime: startIso,
          endTime: endIso,
          avgTime: Number(this.profilingAvgTime) || 0,
          isPoly: !!this.is_water_mask,
        });

        await this.$store.dispatch('fetchTrackPoints', {
          start_time: startIso,
          end_time: endIso,
          limit: 5000,
          raw: true,
        });

        await this.setLoadingDataState(false);
      } catch (error) {
        console.error(error);
        await this.setLoadingDataState(false);
      }
    },
    async get_params () {
      try {
        // Кнопка Search (Map/Fluo/Charts) висит на get_params. В обычном режиме
        // фиксируем правую границу диапазона = «сейчас» на момент клика (если
        // задан только start). Делаем ДО setLoadingDataState, пока пикер активен.
        // В live правую границу оставляем пустой (управляется snapLiveStartToNow).
        if (!this.is_live) this.snapSearchEndToNow();
        await this.setLoadingDataState(true);
        this.heatmap_vm = false;
        if(this.is_custom_data) {
          await this.setDateStartState(this.dateStartIso());
          await this.setDateEndState(this.dateEndIso());
        }
        this.tableDate = this.date;

        const params = {
          start_time: this.dateStartIso(),
          end_time: this.dateEndIso(),
          is_poly: true
        }
        const response = await axios.get('measurements/params', { params: this.withLaser(params) });
        this.mins1 = response.data.all_mins;
        this.mins2 = response.data.all_mins;
        this.mins1_r = response.data.all_mins;
        this.mins2_r = response.data.all_mins;
        this.mins1_g = response.data.all_mins;
        this.mins2_g = response.data.all_mins;
        this.mins1_b = response.data.all_mins;
        this.mins2_b = response.data.all_mins;
        this.cals = response.data.cals;

        // Динамически формируем списки Signal/Normalization из all_params
        // вместо прежнего хардкода. label — человекочитаемое название,
        // min — соответствующий MIN-идентификатор для формул.
        this.applyParamList(response.data.all_params);

        if (this.date && this.date[0]) {
          await this.$store.dispatch('fetchTrackPoints', {
            start_time: this.dateStartIso(),
            end_time: this.dateEndIso(),
            limit: 5000,
            raw: true,
          });
        }



        await this.setLoadingDataState(false);

      } catch(error) {
        console.error(error);
        await this.setLoadingDataState(false);
      }
    },
    async min_changed(value, flag) {
      try {
        const params = {
          start_time: this.dateStartIso(),
          end_time: this.dateEndIso(),
          is_poly: true,
          selected_mins: [value]
        }
        const response = await axios.get('measurements/params', { params: this.withLaser(params) });
        if(flag === 'c1' || !this.selected_min2)
          this.mins2 = response.data.compatible_mins;
        else if (flag === 'c2' || !this.selected_min1)
          this.mins1 = response.data.compatible_mins;
        if(flag === 'r1' || !this.selected_min2_r)
          this.mins2_r = response.data.compatible_mins;
        else if (flag === 'r2' || !this.selected_min1_r)
          this.mins1_r = response.data.compatible_mins;
         if(flag === 'g1' || !this.selected_min2_g)
          this.mins2_g = response.data.compatible_mins;
        else if (flag === 'g2' || !this.selected_min1_g)
          this.mins1_g = response.data.compatible_mins;
         if(flag === 'b1' || !this.selected_min2_b)
          this.mins2_b = response.data.compatible_mins;
        else if (flag === 'b2' || !this.selected_min1_b)
          this.mins1_b = response.data.compatible_mins;
        this.cals = response.data.cals;
        this.formulae_result1 =  this.selected_cal_r + '×' + this.selected_min1_r + '÷' + this.selected_min2_r; 
        this.formulae_result2 =  this.selected_cal_g + '×' + this.selected_min1_g + '÷' + this.selected_min2_g;
        this.formulae_result3 =  this.selected_cal_b + '×' + this.selected_min1_b + '÷' + this.selected_min2_b;

      } catch(error) {
        console.error(error);
        await this.setLoadingDataState(false);
      }
    },
    async loadCompatibleNorms(signalName, otherChannels, compatKey, loadingKey) {
      this[compatKey] = [];
      // Ключ для хранения MIN-строк, совместимых с этим сигналом (по каналу).
      // Используется в draw() для корректного резолва имени нормализации в MIN.
      const minsKey = compatKey.replace('compatibleNorms', 'compatibleNormMins');
      this[minsKey] = [];
      if (!signalName) {
        this[loadingKey] = false;
        return;
      }
      if (!this.date || !this.date[0]) {
        this[loadingKey] = false;
        return;
      }

      const currentMin = await this.findMatchingMin(signalName, this.mins1);
      if (signalName === 'No' || currentMin === '1') {
        const all = [];
        const allSet = new Set(this.mins1 || []);
        for (const name of this.norms) {
          if (name === 'No') continue;
          // Аналогично основной ветке: строгая проверка по (wavelength, channel, hash).
          const baseNum = await this.extractNumberFromName(name);
          const channel = await this.extractChannelIndexFromName(name);
          const hashNum = await this.extractHashNumberFromName(name);
          if (baseNum === null || channel === null || hashNum === null) continue;
          const exactMin = `MIN(${baseNum}_${channel},${hashNum})`;
          if (allSet.has(exactMin)) all.push(name);
        }
        this[compatKey] = all;
        // Без сигнала ограничения по каналу нет — используем полный список.
        this[minsKey] = this.mins1 ? [...this.mins1] : [];
        this[loadingKey] = false;
        return;
      }
      if (!currentMin) {
        this[loadingKey] = false;
        return;
      }

      // MP-каналы независимы: список совместимых Normalization для канала
      // зависит только от его собственного Signal. Выбор Sig/Norm в других
      // MP не сужает варианты — отсутствие совместных точек между каналами
      // допустимо (в таких точках канал просто не вносит вклад в IDW).
      // otherChannels оставлен в сигнатуре для обратной совместимости вызовов.
      void otherChannels;
      const occupiedMins = [currentMin];

      this[loadingKey] = true;
      try {
        const params = {
          start_time: this.dateStartIso(),
          end_time: this.dateEndIso(),
          is_poly: true,
          selected_mins: occupiedMins,
        };
        const response = await axios.get('measurements/params', { params: this.withLaser(params) });
        const compatibleMins = response.data.compatible_mins || [];
        const compatibleSet = new Set(compatibleMins);
        const filtered = [];
        // Строгая фильтрация: имя нормализации попадает в список только если
        // ТОЧНО соответствующая ей MIN (по wavelength + channel + hash) есть в
        // compatibleMins от бэкенда. Раньше использовался findMatchingMin, который
        // при отсутствии точного совпадения откатывался к oldFindMatchingMin —
        // тот игнорирует канал и подбирает любой MIN с такой же длиной волны.
        // Из-за этого имя «650 ... (ch.3, #12)» попадало в дропдаун, когда
        // Signal стоял на канале 3 (фактически разрешалось в MIN(650_2,12)),
        // что вводило пользователя в заблуждение и при некоторых конфигурациях
        // данных приводило к формуле с двумя MIN на одном канале.
        for (const name of this.norms) {
          if (name === 'No') continue;
          const baseNum = await this.extractNumberFromName(name);
          const channel = await this.extractChannelIndexFromName(name);
          const hashNum = await this.extractHashNumberFromName(name);
          if (baseNum === null || channel === null || hashNum === null) continue;
          const exactMin = `MIN(${baseNum}_${channel},${hashNum})`;
          if (compatibleSet.has(exactMin)) filtered.push(name);
        }
        this[compatKey] = filtered;
        // Сохраняем список MIN, ограниченный по каналу/времени/полигону, чтобы
        // resolve имени нормализации в Apply шёл строго по этому списку.
        this[minsKey] = compatibleMins;
      } catch (error) {
        console.error('Failed to load compatible normalizations', error);
        this[compatKey] = [];
      } finally {
        this[loadingKey] = false;
      }
    },
    async onSignalChanged(channel) {
      const upper = channel.toUpperCase();
      this['selectedNorm' + upper] = undefined;
      // MP-каналы независимы: при смене Signal обновляем совместимые
      // Normalization только в текущем MP, других не трогаем.
      const others = ['r', 'g', 'b'].filter(c => c !== channel);
      await this.loadCompatibleNorms(
        this['selectedSig' + upper],
        others,
        'compatibleNorms' + upper,
        'loadingNorms' + upper,
      );
    },
    async onChartsSignalChanged() {
      const ch = this.activeChartsChannel;
      if (ch === 'r' || ch === 'g' || ch === 'b') {
        await this.onSignalChanged(ch);
        return;
      }
      this.chartsTabNorm = '';
      await this.loadCompatibleNorms(
        this.chartsTabSig,
        ['r', 'g', 'b'],
        'compatibleNormsCharts',
        'loadingNormsCharts',
      );
    },
    async extractNumberFromName(str) {
      if (!str)
        return null;
      const match = str.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    },
    async extractBaseNumberFromMin(str) {
      if (!str)
        return null;
      const match = str.match(/MIN\((\d+)_\d+,\d+\)/);
      return match ? parseInt(match[1], 10) : null;
    },
    // Извлекает номер канала из строки. Поддерживаются форматы
    // "(ch 3, ...)", "(ch.3, ...)", "(ch3, ...)" — встречаются оба написания.
    async extractChannelIndexFromName(str) {
      if (!str) return null;
      const match = str.match(/ch[\s.]*?(\d+)/i);
      return match ? parseInt(match[1], 10) : null;
    },

    // Извлекает число после "#" (номер датчика/коэффициента)
    async extractHashNumberFromName(str) {
      if (!str) return null;
      const match = str.match(/#\s*(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    },

    // Извлекает число после подчёркивания в MIN-строке (до запятой)
    async extractMinIndexFromMin(str) {
      if (!str) return null;
      const match = str.match(/_(\d+),/);
      return match ? parseInt(match[1], 10) : null;
    },

    // Извлекает число после запятой в MIN-строке (до закрывающей скобки)
    async extractMinSecondArgFromMin(str) {
      if (!str) return null;
      const match = str.match(/,\s*(\d+)\)/);
      return match ? parseInt(match[1], 10) : null;
    },
    // Применяет список параметров из ответа measurements/params:
    // обновляет дропдауны Signal/Normalization и карту название -> MIN.
    applyParamList(allParams) {
      const list = Array.isArray(allParams) ? allParams : [];
      const labels = list.map(p => p.label);
      this.signals = ['No', ...labels];
      this.norms = ['No', ...labels];
      const labelMap = {};
      for (const p of list) {
        labelMap[p.label] = p.min;
      }
      this.paramMinByLabel = labelMap;
    },
    async findMatchingMin(name, minsList) {
      if (name === "No") return "1";

      // Название пришло из all_params — MIN известен напрямую, парсить текст
      // не нужно. Используем сопоставление только если этот MIN присутствует
      // в переданном (возможно ограниченном по совместимости) списке.
      const mappedMin = this.paramMinByLabel ? this.paramMinByLabel[name] : null;
      if (mappedMin && Array.isArray(minsList) && minsList.includes(mappedMin)) {
        return mappedMin;
      }

      const baseNum = await this.extractNumberFromName(name);
      const channel = await this.extractChannelIndexFromName(name);
      const hashNum = await this.extractHashNumberFromName(name);

      // Строим словарь MIN-строк по составному ключу "база_канал_второйАргумент"
      const minMap = new Map();
      for (const minStr of minsList) {
        const minBase = await this.extractBaseNumberFromMin(minStr);
        const minIndex = await this.extractMinIndexFromMin(minStr);
        const minSecond = await this.extractMinSecondArgFromMin(minStr);
        if (minBase !== null && minIndex !== null && minSecond !== null) {
          minMap.set(`${minBase}_${minIndex}_${minSecond}`, minStr);
        }
      }

      // Пытаемся найти точное совпадение по трём числам
      if (baseNum !== null && channel !== null && hashNum !== null) {
        const key = `${baseNum}_${channel}_${hashNum}`;
        if (minMap.has(key)) return minMap.get(key);
      }

      // Fallback: только база + канал (без учёта второго аргумента)
      if (baseNum !== null && channel !== null) {
        for (const [key, minStr] of minMap.entries()) {
          const [b, ch] = key.split('_').map(Number);
          if (b === baseNum && ch === channel) {
            return minStr;
          }
        }
      }

      // Последний fallback – старый метод (только по базовому числу)
      return await this.oldFindMatchingMin(name, minsList);
    },

    async oldFindMatchingMin(name, minsList) {
      const minMap = new Map();
      for (const minStr of minsList) {
        const baseNum = await this.extractBaseNumberFromMin(minStr);
        if (baseNum !== null) minMap.set(baseNum, minStr);
      }
      const num = await this.extractNumberFromName(name);
      return num !== null ? minMap.get(num) || null : null;
    },

    async draw () {
      // Если уже рисуем - отменяем
      if(this.isDrawing || this.isStarted) {
        await this.cancelDrawing();
        return;
      }
      // if(this.selectedRCal.name === "Chlorophyll Venice" && this.selectedSigR === "685 Chlorophyll-a" && this.selectedNormR === "292 Raman Channel")
      //   this.formulae_result1 =  "2.85" + '×' + "MIN(685_3,1)" + '÷' + "MIN(290_2,10)";

      // Ранее формула умножалась на cal.coeffC как упрощённая «калибровка».
      // Теперь бэкенд применяет полный полином y = a*x^3 + b*x^2 + c*x + d по
      // выбранному calibration_id, поэтому в самой формуле множитель равен 1.
      // Норму резолвим в MIN по ограниченному (по каналу Signal) списку, чтобы
      // имя «X нм» не разрешилось в MIN того же канала, что и Signal, и формула
      // не оказалась противоречивой. Если ограниченный список пуст — fallback на mins2.
      const normMinsR = (this.compatibleNormMinsR && this.compatibleNormMinsR.length) ? this.compatibleNormMinsR : this.mins2;
      const normMinsG = (this.compatibleNormMinsG && this.compatibleNormMinsG.length) ? this.compatibleNormMinsG : this.mins2;
      const normMinsB = (this.compatibleNormMinsB && this.compatibleNormMinsB.length) ? this.compatibleNormMinsB : this.mins2;
      if(this.r_vm)
        this.formulae_result1 = '1×' + await this.findMatchingMin(this.selectedSigR, this.mins1) + '÷' + await this.findMatchingMin(this.selectedNormR, normMinsR);
      if(this.g_vm)
        this.formulae_result2 = '1×' + await this.findMatchingMin(this.selectedSigG, this.mins1) + '÷' + await this.findMatchingMin(this.selectedNormG, normMinsG);
      if(this.b_vm)
        this.formulae_result3 = '1×' + await this.findMatchingMin(this.selectedSigB, this.mins1) + '÷' + await this.findMatchingMin(this.selectedNormB, normMinsB);
      console.log(this.formulae_result1)
      // Установите состояния ДО начала операции
      this.isDrawing = true;
      this.isStarted = false;
      this.idwText = "Apply";

      // Live: отсчёт начинается с момента нажатия Apply. Делаем это ДО
      // applyChartsParams/load, чтобы и интерполяция, и графики стартовали с
      // одной точки, а пикер показывал [now, пусто].
      // Non-live Apply: правую границу пикера НЕ трогаем — логика Apply прежняя
      // (автозаполнение end=now делает только кнопка Search → get_params).
      if (this.is_live) this.snapLiveStartToNow();

      this.applyChartsParams();

      try {
        if(this.is_custom_data) {
          await this.setDateStartState(this.dateStartIso());
          await this.setDateEndState(this.dateEndIso());
        }
        await this.load();
        
        // Проверьте, не была ли отмена запрошена во время загрузки
        if (this.idwAction === 'cancel') {
          await this.cancelDrawing();
          return;
        }
        
        this.isDrawing = true;
        if(this.r_min === "" || this.r_max === "")
          await this.$store.commit('setIsRRange', false);
        if(this.g_min === "" || this.g_max === "")
          await this.$store.commit('setIsGRange', false);
        if(this.b_min === "" || this.b_max === "")
          await this.$store.commit('setIsBRange', false);
        
        this.idwAction = 'start';
      } catch (error) {
        console.error('Error in draw:', error);
        await this.cancelDrawing();
      }
    },
    async cancelDrawing() {
      this.idwAction = 'cancel';
      this.isDrawing = false;
      this.isStarted = false;
      this.idwText = "Apply";

      // Принудительно отправьте событие отмены
      this.$emit('statusDrawing', "taskcancelled", 0);
    },
    applyChartsParams() {
      const titles = this.$store.getters.paramTitles || [];
      const dim = (cal) => {
        const u = cal && cal.rangeUnit ? cal.rangeUnit : '';
        return u ? ', ' + u : ', a.u.';
      };
      const labelOf = (sig, cal, fallback) => {
        const base = (sig === 'No' || !sig) ? (fallback || '') : sig.split('(')[0].trim();
        return base + dim(cal);
      };
      const existing = this.$store.getters['chartsParams/chartsParameters'] || [];
      const colorFor = (id) => {
        const found = existing.find(p => p.id === id);
        return found ? found.color : undefined;
      };
      // Data limits с Map-вкладки прокидываются в авто-чарты, чтобы Charts
      // и Map использовали один и тот же фильтр значений (UI на Map называется
      // «Data limits», на Charts — тоже «Data limits»; это один и тот же
      // параметр, дублировать его в двух местах смысла нет).
      const parameters = [
        {
          id: 'r',
          title: titles[0] || 'Measuring parameter 1',
          enabled: !!this.r_vm,
          color: colorFor('r'),
          signal: this.selectedSigR || '',
          normalization: this.selectedNormR || '',
          calibrationId: this.selectedCalRId || '',
          formula: this.formulae_result1 || '',
          label: labelOf(this.selectedSigR, this.selectedRCal, titles[0] || 'Measuring parameter 1'),
          is_filter: !!this.is_r_filter,
          filter_min: this.r_filter_min !== '' && this.r_filter_min !== null ? Number(this.r_filter_min) : null,
          filter_max: this.r_filter_max !== '' && this.r_filter_max !== null ? Number(this.r_filter_max) : null,
        },
        {
          id: 'g',
          title: titles[1] || 'Measuring parameter 2',
          enabled: !!this.g_vm,
          color: colorFor('g'),
          signal: this.selectedSigG || '',
          normalization: this.selectedNormG || '',
          calibrationId: this.selectedCalGId || '',
          formula: this.formulae_result2 || '',
          label: labelOf(this.selectedSigG, this.selectedGCal, titles[1] || 'Measuring parameter 2'),
          is_filter: !!this.is_g_filter,
          filter_min: this.g_filter_min !== '' && this.g_filter_min !== null ? Number(this.g_filter_min) : null,
          filter_max: this.g_filter_max !== '' && this.g_filter_max !== null ? Number(this.g_filter_max) : null,
        },
        {
          id: 'b',
          title: titles[2] || 'Measuring parameter 3',
          enabled: !!this.b_vm,
          color: colorFor('b'),
          signal: this.selectedSigB || '',
          normalization: this.selectedNormB || '',
          calibrationId: this.selectedCalBId || '',
          formula: this.formulae_result3 || '',
          label: labelOf(this.selectedSigB, this.selectedBCal, titles[2] || 'Measuring parameter 3'),
          is_filter: !!this.is_b_filter,
          filter_min: this.b_filter_min !== '' && this.b_filter_min !== null ? Number(this.b_filter_min) : null,
          filter_max: this.b_filter_max !== '' && this.b_filter_max !== null ? Number(this.b_filter_max) : null,
        },
      ];
      const fetchOptions = {
        startTime: this.date && this.date[0] ? this.dateStartIso() : null,
        // Открытый правый край (live) → конец = «сейчас».
        endTime: this.date && this.date[0] ? this.dateEndIso() : null,
        avgTime: this.avgTime,
      };
      this.$store.dispatch('chartsParams/applyChartsParameters', { parameters, fetchOptions, logScale: !!this.log_vm })
        .catch(err => console.error('Failed to apply charts parameters:', err));
    },
    update_chart_color(chart, color) {
      const updatedCharts = new Set(this.charts);
      updatedCharts.forEach(c => {
        if (c.id === chart.id) c.color = color;
      });
      this.charts = updatedCharts;
      this.colorChart = { id: chart.id, color, ts: Date.now() };
    },
    update_auto_chart_color(id, color) {
      this.$store.commit('chartsParams/SET_CHARTS_PARAM_FIELDS', {
        id,
        fields: { color },
      });
      this.$store.commit('chartsParams/SET_CHARTS_APPLIED_AT', Date.now());
    },
    remove_auto_chart(id) {
      this.$store.commit('chartsParams/DISABLE_CHARTS_PARAM', id);
      this.$store.commit('chartsParams/SET_CHARTS_APPLIED_AT', Date.now());
    },
    set_auto_chart_field(id, field, value) {
      this.$store.commit('chartsParams/SET_CHARTS_PARAM_FIELDS', {
        id,
        fields: { [field]: value },
      });
    },
    // eslint-disable-next-line no-unused-vars
    apply_auto_chart_filter(id) {
      // Bump appliedAt so ChartsView re-evaluates filters for every parameter.
      this.$store.commit('chartsParams/SET_CHARTS_APPLIED_AT', Date.now());
    },
    async set_custom() {
      if (!this.is_custom_data) {
        if (this.$store.getters.stateDateStart && this.$store.getters.stateDateEnd)
          this.setDate([new Date(this.$store.getters.stateDateStart),
                           new Date(this.$store.getters.stateDateEnd)]);
        this.is_custom_data = true;
      }
    },
    async set_complete() {
      try {
        if (this.is_custom_data) {
          this.is_live = false;
          this.isLoadingTimerange = true;
          await this.setLoadingDataState(true);
          let {data} = await axios.get(`time_range`, { params: this.withLaser({}) });

          await this.setTimeRangeState(data);
          if (this.$store.getters.stateCompleteDateStart && this.$store.getters.stateCompleteDateEnd)
            this.setDate([new Date(this.$store.getters.stateCompleteDateStart),
                           new Date(this.$store.getters.stateCompleteDateEnd)]);
          this.is_custom_data = false;
          await this.setLoadingDataState(false);
          this.isLoadingTimerange = false;
        }
      } catch(error) {
        console.error(error);
      }
    },
    calculateStatistics(geojsonData) {
      const results = {
          result13: [],
          result23: [],
          result43: []
      };

      // Collect all numeric values
      geojsonData.features.forEach(feature => {
          ['result13', 'result23', 'result43'].forEach(key => {
              const value = parseFloat(feature.properties[key].trim());
              if (!isNaN(value)) {
                  results[key].push(value);
              }
          });
      });

      // Calculate statistics for a given array
      const calculateStats = (values) => {
          if (values.length === 0) return null;
          
          const sum = values.reduce((a, b) => a + b, 0);
          const sumSquares = values.reduce((a, b) => a + b*b, 0);
          
          return {
              max: Math.max(...values),
              min: Math.min(...values),
              average: sum / values.length,
              rms: Math.sqrt(sumSquares / values.length)
          };
      };

      return {
          result13: calculateStats(results.result13),
          result23: calculateStats(results.result23),
          result43: calculateStats(results.result43),
          length: geojsonData.features.length
      };
  },
  openAddCalibrationModal() {
    this.$refs.calibrationFormModal.open();
  },
  openDescriptionModal() {
    this.$refs.calibrationTableModal.open();
  },
  async saveCalibration(calibration) {
    // Если у записи есть id — это редактирование существующей в БД калибровки.
    if (calibration.id && this.calibrations.find(c => c.id === calibration.id)) {
      try {
        await this.$store.dispatch('updateCalibration', calibration);
      } catch (e) {
        console.error('Failed to update calibration:', e);
      }
    } else {
      try {
        await this.$store.dispatch('addCalibration', calibration);
      } catch (e) {
        console.error('Failed to create calibration:', e);
      }
    }
  },
  editCalibration(cal) {
    this.$refs.calibrationFormModal.open(cal);
  },
  async deleteCalibration(id) {
    try {
      await this.$store.dispatch('deleteCalibration', id);
    } catch (e) {
      console.error('Failed to delete calibration:', e);
    }
    // Если удалённая калибровка была выбрана в каком-то канале – сбросить выбор
  //   if (this.selected_cal_r && this.selected_cal_r.id === id) this.selected_cal_r = null;
  //   if (this.selected_cal_g && this.selected_cal_g.id === id) this.selected_cal_g = null;
  //   if (this.selected_cal_b && this.selected_cal_b.id === id) this.selected_cal_b = null;
  //   this.get_params();
  },
  },
});
</script>
<style type="text/css">

.b-example-divider {
  height: 3rem;
  background-color: rgba(0, 0, 0, .1);
  border: solid rgba(0, 0, 0, .15);
  border-width: 1px 0;
  box-shadow: inset 0 .5em 1.5em rgba(0, 0, 0, .1), inset 0 .125em .5em rgba(0, 0, 0, .15);
}

.b-example-vr {
  flex-shrink: 0;
  width: 0.5rem;
  height: 100vh;
}

.bi {
  vertical-align: -.125em;
  fill: currentColor;
}
.list-group-item {
  padding: 0.2rem;
}
.accordion-body {
  padding: 0.1rem;
}
/* Red Switch */
  .switch-red .form-check-input:checked {
    background-color: #ff0000 !important;
    border-color: #ff0000 !important;
  }
  .switch-red .form-check-input:focus {
    box-shadow: 0 0 0 0.25rem rgba(255, 0, 0, 0.25) !important;
  }
  /* Green Switch */
  .switch-green .form-check-input:checked {
    background-color: #00ff00 !important;
    border-color: #00ff00 !important;
  }
  .switch-green .form-check-input:focus {
    box-shadow: 0 0 0 0.25rem rgba(0, 255, 0, 0.25) !important;
  }

  /* Blue Switch */
  .switch-blue .form-check-input:checked {
    background-color: #0000ff !important;
    border-color: #0000ff !important;
  }
  .switch-blue .form-check-input:focus {
    box-shadow: 0 0 0 0.25rem rgba(0, 0, 255, 0.25) !important;
  }
.no-gutters {
    --bs-gutter-x: 0;
    margin-right: 0;
    margin-left: 0;
    padding: 0 5px;
}
.compact-select {
    padding: 0.375rem 0.75rem;
}
.divider {
    padding: 0 5px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.small-alert {
  padding: 0.25rem 0.5rem; /* Adjust values as needed */
  font-size: 0.875rem; /* Optionally reduce font size */
}
</style>